import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { Calendar, Download, FileText, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import BillCustomerView from '../BillCustomer/BillCustomerView';
import { billApi } from '../BillCustomer/billApi';
import QuotationView from '../EstimationQuotation/QuotationView';
import { quotationApi } from '../EstimationQuotation/quotationApi';
import InvoiceView from '../Invoice/InvoiceView';
import { invoiceApi } from '../Invoice/invoiceApi';
import { purchaseOrderApi } from '../PurchaseOrder/PurchaseOrderApi';
import PurchaseOrderView from '../PurchaseOrder/PurchaseOrderView';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import IconTextButton from '../ui/IconTextButton';
import { fdocApi } from './FdocApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthDateRange = (yearMonth) => {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(yearMonth)) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date();
    return {
      date_from: start.toISOString().split('T')[0],
      date_to: end.toISOString().split('T')[0],
    };
  }

  const [year, month] = yearMonth.split('-').map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return {
    date_from: start.toISOString().split('T')[0],
    date_to: end.toISOString().split('T')[0],
  };
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

/** Fdoc type value: loads vendor/customer FDOC order history from GET /fdoc/order-history */
const FDOC_ORDER_HISTORY_TYPE = 'FDOC Order History';

const mapFdocStatusToDisplay = (status) => {
  const statusMap = {
    uploaded: 'Pending',
    extracting: 'Processing',
    extracted: 'Active',
    extraction_failed: 'Failed',
    confirmed: 'Completed',
  };
  return statusMap[status] || status || '—';
};

const normalizeOrderHistoryDocType = (t) => {
  if (!t) return 'Document';
  const u = String(t).toUpperCase();
  if (u === 'PO') return 'Purchase Order';
  if (u === 'ESTIMATION') return 'Estimation';
  if (u === 'INVOICE') return 'Invoice';
  if (u === 'BILL') return 'Bill';
  return t;
};

const encodeVendorFilterValue = (v) => {
  if (v.vendor_id) return `id:${v.vendor_id}`;
  if (v.vendor_name) return `name:${encodeURIComponent(v.vendor_name)}`;
  return '';
};

const encodeCustomerFilterValue = (c) => {
  if (c.customer_id) return `id:${c.customer_id}`;
  if (c.customer_name) return `name:${encodeURIComponent(c.customer_name)}`;
  return '';
};

const parseVendorFilterSelection = (value) => {
  if (!value) return {};
  if (value.startsWith('id:')) return { vendor_id: value.slice(3) };
  if (value.startsWith('name:')) return { vendor_name: decodeURIComponent(value.slice(5)) };
  return { vendor_id: value };
};

const parseCustomerFilterSelection = (value) => {
  if (!value) return {};
  if (value.startsWith('id:')) return { customer_id: value.slice(3) };
  if (value.startsWith('name:')) return { customer_name: decodeURIComponent(value.slice(5)) };
  return { customer_id: value };
};

const transformOrderHistoryEntry = (entry) => {
  const docDate = entry.document_date || (entry.uploaded_at ? entry.uploaded_at.split('T')[0] : '');
  const uploadedDate = entry.uploaded_at ? entry.uploaded_at.split('T')[0] : docDate;
  const fid = entry.fdoc_id || '';
  return {
    id: fid,
    fdoc_id: fid,
    docNumber: entry.reference_number || `FDOC-${fid.substring(0, 8).toUpperCase()}`,
    date: docDate || uploadedDate,
    totalAmount: entry.total_amount != null ? Number(entry.total_amount) : 0,
    taxAmount: entry.tax_total != null ? Number(entry.tax_total) : 0,
    status: mapFdocStatusToDisplay(entry.status),
    apiStatus: entry.status,
    description: `${normalizeOrderHistoryDocType(entry.document_type)} — ${entry.file_name || entry.reference_number || fid}`,
    file_name: entry.file_name,
    file_type: entry.file_type,
    extracted_data: null,
    documentType: normalizeOrderHistoryDocType(entry.document_type),
    siteId: null,
    orderHistoryEntry: true,
    originalDoc: entry,
  };
};

const Fdoc = () => {
  // Log screen view
  useScreenLogging('Fdoc', {
    component: 'Fdoc',
  });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedFdocType, setSelectedFdocType] = useState('');
  const [orderHistoryKind, setOrderHistoryKind] = useState('both');
  const [esignedOnly, setEsignedOnly] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderHistoryFilters, setOrderHistoryFilters] = useState({
    vendors: [],
    customers: [],
  });
  const [orderHistoryFiltersLoading, setOrderHistoryFiltersLoading] = useState(false);
  const [orderHistoryFiltersError, setOrderHistoryFiltersError] = useState('');
  const [orderHistoryListKind, setOrderHistoryListKind] = useState('vendor');
  const [selectedOrderHistoryVendorId, setSelectedOrderHistoryVendorId] = useState('');
  const [selectedOrderHistoryCustomerId, setSelectedOrderHistoryCustomerId] = useState('');
  const [orderHistoryLastKey, setOrderHistoryLastKey] = useState(null);
  const [orderHistorySummary, setOrderHistorySummary] = useState({
    total_amount_sum: 0,
    tax_total_sum: 0,
  });
  const [orderHistoryLoadingMore, setOrderHistoryLoadingMore] = useState(false);
  
  // Purchase Order modal state
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState({});
  const [isLoadingPurchaseOrderDetails, setIsLoadingPurchaseOrderDetails] = useState(false);
  const fetchingPurchaseOrderRef = useRef(false);
  
  // Invoice modal state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [isLoadingInvoiceDetails, setIsLoadingInvoiceDetails] = useState(false);
  const fetchingInvoiceRef = useRef(false);
  
  // Bill modal state
  const [selectedBill, setSelectedBill] = useState(null);
  const [billDetails, setBillDetails] = useState({});
  const [isLoadingBillDetails, setIsLoadingBillDetails] = useState(false);
  const fetchingBillRef = useRef(false);

  // Cancel (soft-delete) FDOC in progress
  const [cancellingFdocId, setCancellingFdocId] = useState(null);
  const autoCancelledFdocIdsRef = useRef(new Set());
  const autoCancelMissingFdoc = async (fdocId, reason = 'file missing, auto-cancelled from UI') => {
    if (!fdocId) return false;
    if (autoCancelledFdocIdsRef.current.has(fdocId)) return true;
    autoCancelledFdocIdsRef.current.add(fdocId);
    try {
      await fdocApi.cancel(fdocId, { reason });
      return true;
    } catch (e) {
      // If cancel fails, allow retry later; don't surface user-facing errors for background cleanup.
      autoCancelledFdocIdsRef.current.delete(fdocId);
      console.warn('[Fdoc] Auto-cancel failed for missing FDOC:', fdocId, e?.message || e);
      return false;
    }
  };
  
  // Get overlay functions from context
  const { openOverlay } = useOverlay();
  const location = useLocation();
  const navigate = useNavigate();

  // Restore Fdoc type and site when returning from Create Invoice (breadcrumb back)
  useEffect(() => {
    const state = location.state;
    if (!state?.restoreFdoc) return;
    if (state.selectedFdocType != null && state.selectedFdocType !== '') {
      setSelectedFdocType(state.selectedFdocType);
    }
    if (state.selectedSite != null) {
      setSelectedSite(state.selectedSite);
    }
    if (state.selectedMonth != null && state.selectedMonth !== '') {
      setSelectedMonth(state.selectedMonth);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state]);

  // Fetch vendor/customer options for FDOC order history filters.
  useEffect(() => {
    const fetchOrderHistoryFilters = async () => {
      setOrderHistoryFiltersLoading(true);
      setOrderHistoryFiltersError('');
      try {
        const { date_from, date_to } = getMonthDateRange(selectedMonth);
        const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
        const result = await fdocApi.getOrderHistoryFilters({
          kind: orderHistoryKind,
          date_from,
          date_to,
          status: 'confirmed,extracted',
          site_id: siteId || undefined,
          limit: 200,
        });
        setOrderHistoryFilters({
          vendors: result.vendors || [],
          customers: result.customers || [],
        });
      } catch (err) {
        console.warn('[Fdoc] Failed to fetch order-history filters:', err?.message || err);
        setOrderHistoryFilters({ vendors: [], customers: [] });
        setOrderHistoryFiltersError(
          err?.message || 'Unable to load party filters. Please try changing month or site.'
        );
      } finally {
        setOrderHistoryFiltersLoading(false);
      }
    };

    fetchOrderHistoryFilters();
  }, [orderHistoryKind, selectedMonth, selectedSite]);

  useEffect(() => {
    if (orderHistoryKind === 'vendor') setOrderHistoryListKind('vendor');
    else if (orderHistoryKind === 'customer') setOrderHistoryListKind('customer');
  }, [orderHistoryKind]);

  // Fdoc type options - Map to document types
  const fdocTypeOptions = [
    { value: 'Bill', label: 'Bill' },
    { value: 'Invoice', label: 'Invoice' },
    { value: 'Purchase Order', label: 'Purchase Orders' },
    { value: 'Estimation', label: 'Estimation/Quotation' },
    { value: 'Amenity Booking Invoice', label: 'Amenity Booking Invoice' },
    { value: 'Bill OO', label: 'Bill OO' },
    { value: 'Estimation OO', label: 'Estimation/Quotation (OO)' },
    { value: 'Purchase Order OO', label: 'Purchase Order OO' },
    { value: 'Invoice OO', label: 'Invoice OO' },
    { value: FDOC_ORDER_HISTORY_TYPE, label: 'Order history (FDOC)' },
  ];

  // Cancel (soft-delete) a bad FDOC (e.g. 410 file_missing). Removes from list and closes modal on success.
  const handleCancelFdoc = async (fdocId) => {
    if (!fdocId || cancellingFdocId) return;
    setCancellingFdocId(fdocId);
    try {
      await fdocApi.cancel(fdocId, { reason: 'file missing, deleted from UI' });
      setDocuments((prev) => prev.filter((d) => (d.fdoc_id || d.id) !== fdocId));
      if ((selectedPurchaseOrder?.fdoc_id || selectedPurchaseOrder?.id) === fdocId) {
        setSelectedPurchaseOrder(null);
        setPurchaseOrderDetails((prev) => {
          const next = { ...prev };
          delete next[fdocId];
          return next;
        });
      }
      if ((selectedInvoice?.fdoc_id || selectedInvoice?.id) === fdocId) {
        setSelectedInvoice(null);
        setInvoiceDetails((prev) => {
          const next = { ...prev };
          delete next[fdocId];
          return next;
        });
      }
      if ((selectedBill?.fdoc_id || selectedBill?.id || selectedBill?.billId) === fdocId) {
        setSelectedBill(null);
        setBillDetails((prev) => {
          const next = { ...prev };
          delete next[fdocId];
          return next;
        });
      }
    } catch (err) {
      console.error('[Fdoc] Cancel document failed:', err);
      setError(err.message || 'Failed to delete document');
    } finally {
      setCancellingFdocId(null);
    }
  };

  // Helper function to normalize date to YYYY-MM-DD format
  const normalizeDate = (dateValue) => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    
    // If already in YYYY-MM-DD format
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    
    // Try to parse as Date
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      console.warn('Failed to parse date:', dateValue);
    }
    
    return new Date().toISOString().split('T')[0];
  };

  // Modal variants for animations
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  // Helper function to map Bill to UI format
  const mapBillToUI = (apiBill, sites = []) => {
    const billDate = apiBill.bill_timestamp 
      ? new Date(apiBill.bill_timestamp).toISOString().split('T')[0]
      : apiBill.created_at 
        ? new Date(apiBill.created_at).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

    const siteId = apiBill.site_id || '';
    const site = sites.find(s => resolveSiteValue(s) === siteId);
    const siteName = site?.site_name || site?.name || apiBill.site_name || 'N/A';

    return {
      id: apiBill.bill_id,
      billId: apiBill.bill_id,
      orderNumber: apiBill.order_id || '',
      billNumber: apiBill.bill_number || '',
      date: billDate,
      tableNumber: apiBill.table_number || '',
      siteId: siteId,
      siteName: siteName,
      customerName: apiBill.customer?.name || '',
      customerMobile: apiBill.customer?.mobile || '',
      customerEmail: apiBill.customer?.email || '',
      customerId: apiBill.customer_id || null,
      items: (apiBill.line_items || []).map(item => ({
        name: item.item_name || item.name || item.description || '',
        quantity: item.quantity || 0,
        unit: item.unit || 'pcs',
        rate: item.unit_price || item.rate || 0,
        amount: item.line_total || item.amount || 0,
      })),
      subtotal: apiBill.subtotal || 0,
      discountCode: apiBill.discount_code || '',
      discountValue: apiBill.discount_amount || 0,
      discountedSubtotal: (apiBill.subtotal || 0) - (apiBill.discount_amount || 0),
      cgstRate: apiBill.cgst && apiBill.subtotal ? ((apiBill.cgst / (apiBill.subtotal - (apiBill.discount_amount || 0))) * 100).toFixed(1) : 0,
      sgstRate: apiBill.sgst && apiBill.subtotal ? ((apiBill.sgst / (apiBill.subtotal - (apiBill.discount_amount || 0))) * 100).toFixed(1) : 0,
      cgstAmount: apiBill.cgst || 0,
      sgstAmount: apiBill.sgst || 0,
      grandTotal: apiBill.total || 0,
      lineItemsCount: apiBill.line_items?.length || apiBill.line_items_count || 0,
    };
  };

  // Helper function to map Purchase Order to UI format
  const mapPurchaseOrderToUI = (apiPo) => {
    // Calculate totals from line items
    let subtotal = 0;
    let discount = 0;
    const items = (apiPo.lines || []).map((line) => {
      const quantity = line.quantity || line.quantity_ordered || 0;
      const unitCost = line.unit_cost || line.rate || 0;
      const taxRate = line.tax_rate || 0;
      const taxAmount = line.tax_amount || (quantity * unitCost * taxRate / 100);
      const lineTotal = (quantity * unitCost) + taxAmount;
      subtotal += lineTotal;
      
      return {
        name: line.description || line.item_name || 'Unknown Item',
        hsnCode: line.hsn_code || '',
        units: quantity,
        rate: unitCost,
        tax: taxRate,
        amount: lineTotal,
      };
    });

    // Calculate grand total
    const freightTotal = parseFloat(apiPo.freight_total || apiPo.freight_charges || 0);
    const apiSubtotal = parseFloat(apiPo.subtotal || 0);
    const apiTaxTotal = parseFloat(apiPo.tax_total || 0);
    const apiTotal = parseFloat(apiPo.total_amount || apiPo.grand_total || apiPo.total || 0);
    
    // Use calculated subtotal if items exist, otherwise use API subtotal
    const finalSubtotal = items.length > 0 ? subtotal : (apiSubtotal || subtotal);
    const finalTaxTotal = items.length > 0 ? (subtotal - finalSubtotal) : apiTaxTotal;
    const grandTotal = apiTotal > 0 ? apiTotal : (finalSubtotal - discount + freightTotal + finalTaxTotal);
    
    console.log('[Fdoc] Calculated totals:', {
      finalSubtotal,
      discount,
      freightTotal,
      finalTaxTotal,
      grandTotal
    });

    // Extract billing address
    const billingAddress = apiPo.billing_address || {};
    
    return {
      id: apiPo.po_id || apiPo.id,
      poNumber: apiPo.po_number || apiPo.poNumber || 'N/A',
      vendor: apiPo.vendor_name || apiPo.vendor || 'Unknown Vendor',
      poDate: apiPo.po_date || apiPo.poDate || '',
      expectedDeliveryDate: apiPo.expected_delivery_date || apiPo.expectedDeliveryDate || '',
      paymentDate: apiPo.payment_date || apiPo.paymentDate || '',
      paymentMode: apiPo.payment_terms || apiPo.paymentTerms || apiPo.payment_mode || apiPo.paymentMode || 'Not specified',
      siteId: apiPo.site_id || apiPo.siteId || '',
      siteName: apiPo.site_name || apiPo.siteName || '',
      billingAddress: {
        firstName: billingAddress.first_name || billingAddress.firstName || '',
        lastName: billingAddress.last_name || billingAddress.lastName || '',
        email: billingAddress.email || '',
        address: billingAddress.address || billingAddress.street || '',
        city: billingAddress.city || '',
        state: billingAddress.state || '',
        country: billingAddress.country || 'India',
        pinCode: billingAddress.pin_code || billingAddress.pinCode || billingAddress.zip || '',
        mobileNumber: billingAddress.mobile_number || billingAddress.mobileNumber || billingAddress.phone || '',
        panNumber: billingAddress.pan_number || billingAddress.panNumber || '',
        gstNumber: billingAddress.gst_number || billingAddress.gstNumber || '',
      },
      items: items || [],
      subtotal: finalSubtotal,
      discount: parseFloat(apiPo.discount || discount || 0),
      grandTotal: grandTotal,
      status: apiPo.status || 'Pending',
    };
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Split notes so Terms of Sale never appears in Special Notes (display only special notes in first box, only terms in second)
  const splitNotesAndTerms = (notesStr, explicitTerms = '') => {
    const notes = (notesStr && String(notesStr).trim()) || '';
    const termsGiven = explicitTerms != null && String(explicitTerms).trim() !== '';
    if (termsGiven) {
      const terms = String(explicitTerms).trim();
      let special = notes;
      if (terms && notes.includes(terms)) {
        special = notes.replace(terms, '').replace(/\n\n+/g, '\n').trim();
      }
      return { specialNotes: special, termsOfSale: terms };
    }
    if (!notes) return { specialNotes: '', termsOfSale: '' };
    const termsMatch = notes.match(/\bTerms\s+of\s+Sale\s*:?\s*\n?([\s\S]*?)(?=\n\n|\n[A-Z]|\z)/i) || notes.match(/\bTerms\s+of\s+Sale\s*:?\s*([^\n]*)/i);
    if (termsMatch) {
      const terms = termsMatch[1].trim();
      const special = notes.replace(/\bTerms\s+of\s+Sale\s*:?\s*\n?[\s\S]*/i, '').trim();
      return { specialNotes: special, termsOfSale: terms };
    }
    const lines = notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length >= 2) {
      return { specialNotes: lines[0], termsOfSale: lines.slice(1).join('\n') };
    }
    if (lines.length === 1) return { specialNotes: lines[0], termsOfSale: '' };
    return { specialNotes: notes, termsOfSale: '' };
  };

  // Helper function to map Invoice to UI format (for Invoice OO)
  const mapInvoiceToUI = (apiInvoice, apiLines = []) => {
    const billingAddress = apiInvoice.billing_address || {};
    const shippingAddress = apiInvoice.shipping_address || {};
    const rawNotes = apiInvoice.notes || '';
    const explicitTerms = apiInvoice.terms_of_sale ?? apiInvoice.termsOfSale ?? '';
    const { specialNotes, termsOfSale } = splitNotesAndTerms(rawNotes, explicitTerms);
    const rawShipDate = apiInvoice.shipping_details?.estimated_ship_date ?? apiInvoice.estimated_ship_date ?? apiInvoice.ship_date ?? apiInvoice.estimatedShipDate ?? '';
    const estimatedShipDate = rawShipDate ? (typeof rawShipDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawShipDate) ? rawShipDate : (() => { const d = new Date(rawShipDate); return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]; })()) : '';
    const discountType = apiInvoice.discount_type ?? apiInvoice.discountType ?? (apiInvoice.discount_percentage != null || apiInvoice.discount_percent != null ? 'percentage' : 'percentage');
    const discountValue = apiInvoice.discount_value ?? apiInvoice.discount_percentage ?? apiInvoice.discount_percent ?? apiInvoice.discount_pct ?? (apiInvoice.discount != null && (apiInvoice.discount_type === 'percentage' || apiInvoice.discountType === 'percentage') ? apiInvoice.discount : '');

    return {
      id: apiInvoice.invoice_id || apiInvoice.id,
      invoiceNumber: apiInvoice.invoice_number || apiInvoice.invoiceNumber || 'N/A',
      date: apiInvoice.invoice_date || apiInvoice.date || '',
      client: apiInvoice.customer_name || apiInvoice.customerName || 'Unknown Customer',
      customerId: apiInvoice.customer_id || apiInvoice.customerId || null,
      siteId: apiInvoice.site_id || apiInvoice.siteId || null,
      siteName: apiInvoice.site_name || apiInvoice.siteName || 'N/A',
      billingAddress: billingAddress.street || billingAddress.address
        ? `${billingAddress.street || billingAddress.address || ''}, ${billingAddress.city || ''}, ${billingAddress.state || ''} ${billingAddress.pincode || billingAddress.pinCode || ''}`
        : 'N/A',
      shippingAddress: shippingAddress.street || shippingAddress.address
        ? `${shippingAddress.street || shippingAddress.address || ''}, ${shippingAddress.city || ''}, ${shippingAddress.state || ''} ${shippingAddress.pincode || shippingAddress.pinCode || ''}`
        : 'N/A',
      poNumber: apiInvoice.po_number || apiInvoice.poNumber || undefined,
      paymentMode: (apiInvoice.payment_mode || apiInvoice.paymentMode) ? (apiInvoice.payment_mode || apiInvoice.paymentMode) : undefined,
      modeOfTransaction: apiInvoice.mode_of_transaction || apiInvoice.modeOfTransaction || undefined,
      transactionId: apiInvoice.transaction_id || apiInvoice.transactionId || undefined,
      amountPaid: apiInvoice.amount_paid != null && apiInvoice.amount_paid !== '' ? Number(apiInvoice.amount_paid) : undefined,
      paymentTerms: apiInvoice.payment_terms || apiInvoice.paymentTerms || 'N/A',
      lineItems: (apiLines || []).map(line => ({
        name: line.description || line.item_name || line.name || '',
        quantity: line.quantity || 0,
        units: line.unit || line.units || '',
        rate: line.unit_price || line.rate || 0,
        amount: line.line_total || line.line_subtotal || line.amount || 0,
      })),
      subtotal: parseFloat(apiInvoice.subtotal || 0),
      discount: parseFloat(apiInvoice.discount || 0),
      shippingHandling: parseFloat(apiInvoice.shipping_total || apiInvoice.shipping_handling || 0),
      insurance: parseFloat(apiInvoice.insurance || 0),
      totalTax: parseFloat(apiInvoice.tax_total || apiInvoice.tax_amount || 0),
      grandTotal: parseFloat(apiInvoice.grand_total || apiInvoice.total_amount || 0),
      status: apiInvoice.status === 'paid' ? 'Paid' : 
              apiInvoice.status === 'overdue' ? 'Overdue' : 
              apiInvoice.status === 'sent' ? 'Sent' : 
              apiInvoice.status === 'cancelled' ? 'Cancelled' : 'Draft',
      paymentStatus: apiInvoice.payment_status || apiInvoice.paymentStatus || 'unpaid',
      dueDate: apiInvoice.due_date || apiInvoice.dueDate || '',
      currency: apiInvoice.currency || 'INR',
      notes: rawNotes,
      specialNotes,
      termsOfSale,
      estimatedShipDate: estimatedShipDate || '',
      discountType: discountType || 'percentage',
      discountValue: discountValue != null && discountValue !== '' ? String(discountValue) : '',
    };
  };

  // Helper function to handle Purchase Order PDF download
  const handlePurchaseOrderDownload = (po) => {
    if (!po) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('PURCHASE ORDER', 18, true);
      yPosition += 3;
      if (po.siteName) {
        centerText(po.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // PO Information
      addText('PO INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('PO Number:', po.poNumber || 'N/A', 10);
      addTwoColumn('PO Date:', po.poDate || 'N/A', 10);
      if (po.expectedDeliveryDate) {
        addTwoColumn('Expected Delivery:', po.expectedDeliveryDate, 10);
      }
      if (po.paymentDate) {
        addTwoColumn('Payment Date:', po.paymentDate, 10);
      }
      if (po.paymentMode) {
        addTwoColumn('Payment Mode:', po.paymentMode, 10);
      }
      if (po.siteName) {
        addTwoColumn('Site:', po.siteName, 10);
      }
      addTwoColumn('Status:', po.status || 'N/A', 10);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Vendor Information
      if (po.vendor || po.billingAddress) {
        addText('VENDOR INFORMATION', 12, true);
        yPosition += 2;
        if (po.vendor) {
          addTwoColumn('Vendor:', po.vendor, 10);
        }
        if (po.billingAddress) {
          const addr = po.billingAddress;
          if (addr.firstName || addr.lastName) {
            addTwoColumn('Name:', `${addr.firstName || ''} ${addr.lastName || ''}`.trim(), 10);
          }
          if (addr.email) {
            addTwoColumn('Email:', addr.email, 10);
          }
          if (addr.address) {
            addTwoColumn('Address:', addr.address, 10);
          }
          if (addr.city || addr.state) {
            addTwoColumn('City/State:', `${addr.city || ''}, ${addr.state || ''}`.trim(), 10);
          }
          if (addr.mobileNumber) {
            addTwoColumn('Mobile:', addr.mobileNumber, 10);
          }
          if (addr.gstNumber) {
            addTwoColumn('GST:', addr.gstNumber, 10);
          }
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('ITEMS', 12, true);
      yPosition += 3;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Name', margin + 20, yPosition);
      pdf.text('HSN', margin + 80, yPosition);
      pdf.text('Units', margin + 100, yPosition);
      pdf.text('Rate', margin + 120, yPosition);
      pdf.text('Tax%', margin + 140, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (po.items && po.items.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        po.items.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const itemLines = pdf.splitTextToSize(itemName, 50);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text(item.hsnCode || 'N/A', margin + 80, yPosition);
          pdf.text(item.units || 'N/A', margin + 100, yPosition);
          pdf.text(formatCurrency(item.rate || 0), margin + 120, yPosition);
          pdf.text(`${item.tax || 0}%`, margin + 140, yPosition);
          pdf.text(formatCurrency(item.amount || 0), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary
      addText('SUMMARY', 12, true);
      yPosition += 2;
      addTwoColumn('Subtotal:', formatCurrency(po.subtotal || 0), 10);
      if (po.discount > 0) {
        addTwoColumn('Discount:', formatCurrency(po.discount || 0), 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;
      addTwoColumn('GRAND TOTAL:', formatCurrency(po.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      centerText('Thank you!', 10);

      pdf.save(`purchase-order-${po.poNumber || po.id || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Failed to generate purchase order PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  // Helper function to handle Bill PDF download
  const handleBillPdfDownload = (bill) => {
    if (!bill) return;

    try {
      // Create A4 PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      // Helper function to center text
      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to add left-aligned text
      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to add right-aligned text
      const addRightText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        pdf.text(text, pageWidth - margin - textWidth, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to add two-column text (left and right)
      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to draw line
      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('BILL', 18, true);
      yPosition += 3;
      if (bill.siteName) {
        centerText(bill.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // Bill Information
      addText('BILL INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('Bill Number:', bill.billNumber || bill.orderNumber || bill.docNumber || 'N/A', 10);
      if (bill.orderNumber) {
        addTwoColumn('Order Number:', bill.orderNumber, 10);
      }
      addTwoColumn('Date:', bill.date || 'N/A', 10);
      if (bill.tableNumber) {
        addTwoColumn('Table Number:', bill.tableNumber, 10);
      }
      if (bill.siteName) {
        addTwoColumn('Site:', bill.siteName, 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Customer Information
      if (bill.customerName) {
        addText('CUSTOMER INFORMATION', 12, true);
        yPosition += 2;
        addTwoColumn('Name:', bill.customerName, 10);
        if (bill.customerMobile) {
          addTwoColumn('Mobile:', bill.customerMobile, 10);
        }
        if (bill.customerEmail) {
          addTwoColumn('Email:', bill.customerEmail, 10);
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('LINE ITEMS', 12, true);
      yPosition += 3;

      // Items Table Header
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Item Name', margin + 20, yPosition);
      pdf.text('Qty', margin + 100, yPosition);
      pdf.text('Rate', margin + 120, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (bill.items && bill.items.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        bill.items.forEach((item, index) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const quantity = item.quantity || 0;
          const rate = item.rate || 0;
          const amount = item.amount || 0;

          // Wrap item name if too long
          const itemLines = pdf.splitTextToSize(itemName, 70);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text(quantity.toString(), margin + 100, yPosition);
          pdf.text(formatCurrency(rate), margin + 120, yPosition);
          pdf.text(formatCurrency(amount), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Check if we need a new page for summary
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary Section
      addText('SUMMARY', 12, true);
      yPosition += 2;

      if (bill.subtotal !== undefined) {
        addTwoColumn('Subtotal:', formatCurrency(bill.subtotal), 10);
      }

      if (bill.discountValue > 0) {
        addTwoColumn(`Discount (${bill.discountCode || 'N/A'}):`, `-${formatCurrency(bill.discountValue)}`, 10);
      }

      if (bill.cgstAmount > 0) {
        addTwoColumn(`CGST (${bill.cgstRate || 0}%):`, formatCurrency(bill.cgstAmount), 10);
      }

      if (bill.sgstAmount > 0) {
        addTwoColumn(`SGST (${bill.sgstRate || 0}%):`, formatCurrency(bill.sgstAmount), 10);
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Grand Total
      addTwoColumn('GRAND TOTAL:', formatCurrency(bill.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      // Footer
      centerText('Thank you for your business!', 10);
      yPosition += 2;

      // Save PDF
      const fileName = `bill-${bill.billNumber || bill.orderNumber || bill.id || 'unknown'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Failed to generate bill PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Helper function to handle Invoice PDF download
  const handleInvoicePdfDownload = (invoice) => {
    if (!invoice) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('INVOICE', 18, true);
      yPosition += 3;
      if (invoice.siteName) {
        centerText(invoice.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // Invoice Information
      addText('INVOICE INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('Invoice Number:', invoice.invoiceNumber || 'N/A', 10);
      addTwoColumn('Invoice Date:', invoice.date || 'N/A', 10);
      if (invoice.dueDate) {
        addTwoColumn('Due Date:', invoice.dueDate, 10);
      }
      if (invoice.paymentTerms) {
        addTwoColumn('Payment Terms:', invoice.paymentTerms, 10);
      }
      if (invoice.paymentMode) {
        addTwoColumn('Payment Mode:', invoice.paymentMode, 10);
      }
      if (invoice.siteName) {
        addTwoColumn('Site:', invoice.siteName, 10);
      }
      addTwoColumn('Status:', invoice.status || 'N/A', 10);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Customer Information
      if (invoice.client || invoice.billingAddress) {
        addText('CUSTOMER INFORMATION', 12, true);
        yPosition += 2;
        if (invoice.client) {
          addTwoColumn('Client:', invoice.client, 10);
        }
        if (invoice.billingAddress) {
          const addrLines = pdf.splitTextToSize(invoice.billingAddress, 80);
          pdf.setFontSize(10);
          pdf.text('Billing Address:', margin, yPosition);
          yPosition += lineHeight;
          pdf.setFontSize(9);
          addrLines.forEach(line => {
            pdf.text(line, margin + 5, yPosition);
            yPosition += lineHeight - 1;
          });
        }
        if (invoice.shippingAddress && invoice.shippingAddress !== 'N/A') {
          yPosition += 2;
          pdf.setFontSize(10);
          pdf.text('Shipping Address:', margin, yPosition);
          yPosition += lineHeight;
          const shipLines = pdf.splitTextToSize(invoice.shippingAddress, 80);
          pdf.setFontSize(9);
          shipLines.forEach(line => {
            pdf.text(line, margin + 5, yPosition);
            yPosition += lineHeight - 1;
          });
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('LINE ITEMS', 12, true);
      yPosition += 3;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Description', margin + 20, yPosition);
      pdf.text('Qty', margin + 90, yPosition);
      pdf.text('Units', margin + 105, yPosition);
      pdf.text('Rate', margin + 125, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (invoice.lineItems && invoice.lineItems.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        invoice.lineItems.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const itemLines = pdf.splitTextToSize(itemName, 60);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text((item.quantity || 0).toString(), margin + 90, yPosition);
          pdf.text(item.units || 'N/A', margin + 105, yPosition);
          pdf.text(formatCurrency(item.rate || 0), margin + 125, yPosition);
          pdf.text(formatCurrency(item.amount || 0), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No line items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary
      addText('SUMMARY', 12, true);
      yPosition += 2;
      addTwoColumn('Subtotal:', formatCurrency(invoice.subtotal || 0), 10);
      if (invoice.discount > 0) {
        addTwoColumn('Discount:', formatCurrency(invoice.discount || 0), 10);
      }
      if (invoice.shippingHandling > 0) {
        addTwoColumn('Shipping & Handling:', formatCurrency(invoice.shippingHandling || 0), 10);
      }
      if (invoice.totalTax > 0) {
        addTwoColumn('Tax:', formatCurrency(invoice.totalTax || 0), 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;
      addTwoColumn('GRAND TOTAL:', formatCurrency(invoice.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      centerText('Thank you for your business!', 10);

      pdf.save(`invoice-${invoice.invoiceNumber || invoice.id || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Failed to generate invoice PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Helper function to transform external organization documents to Fdoc format
  const transformExternalDoc = (doc, docType, docIdField, docNumberField, dateField) => {
    const docId = doc[docIdField] || doc.id;
    const docNumber = doc[docNumberField] || doc.number || `DOC-${docId?.substring(0, 8)?.toUpperCase() || 'N/A'}`;
    const rawDate = doc[dateField] || doc.date || doc.created_at || doc.invoice_date || doc.po_date || doc.issued_date;
    const docDate = normalizeDate(rawDate);
    
    // Extract amounts based on document type
    let totalAmount = 0;
    let taxAmount = 0;
    
    if (docType === 'Bill OO') {
      totalAmount = parseFloat(doc.total || doc.grand_total || 0);
      taxAmount = (parseFloat(doc.cgst || 0) + parseFloat(doc.sgst || 0));
    } else if (docType === 'Invoice OO') {
      totalAmount = parseFloat(doc.grand_total || doc.total || 0);
      taxAmount = parseFloat(doc.tax_total || doc.tax_amount || 0);
    } else if (docType === 'Estimation OO') {
      totalAmount = parseFloat(doc.grand_total || doc.total || 0);
      taxAmount = parseFloat(doc.tax_total || doc.tax_amount || 0);
    } else if (docType === 'Purchase Order OO') {
      totalAmount = parseFloat(doc.grand_total || doc.total || 0);
      taxAmount = parseFloat(doc.tax_total || doc.tax_amount || 0);
    } else if (docType === 'Amenity Booking Invoice') {
      totalAmount = parseFloat(doc.grand_total || doc.total || 0);
      taxAmount = parseFloat(doc.tax_total || doc.tax_amount || 0);
    }
    
    return {
      id: docId,
      fdoc_id: docId, // Use same ID for compatibility
      docNumber,
      date: docDate, // Already normalized to YYYY-MM-DD
      totalAmount,
      taxAmount,
      status: 'Active', // External org docs are considered active
      apiStatus: doc.status || 'active',
      description: `${docType} - ${docNumber}`,
      file_name: doc.file_name || `${docNumber}.pdf`,
      file_type: doc.file_type || 'pdf',
      extracted_data: null,
      documentType: docType,
      externalDoc: true, // Flag to indicate this is from external API
      originalDoc: doc, // Store original document for reference
    };
  };

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        
        const sorted = [...sitesList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        
        setSites(sorted.length > 0 ? sorted : []);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSites([]);
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch documents from external organization APIs
  const fetchExternalDocuments = async (docType, siteId = null, startDate = null, endDate = null, esignStatus = null) => {
    try {
      let documents = [];
      console.log(`[Fdoc] Fetching ${docType} documents...`, { siteId, startDate, endDate, esignStatus });
      
      // Build common parameters for all OO document types
      const commonParams = {
        limit: 500,
      };
      
      // Add site_id if provided
      if (siteId) {
        commonParams.site_id = siteId;
      }
      
      // Add date range if provided
      if (startDate) {
        commonParams.start_date = startDate;
      }
      if (endDate) {
        commonParams.end_date = endDate;
      }
      if (esignStatus) {
        commonParams.esign_status = esignStatus;
      }
      
      if (docType === 'Bill OO') {
        // For Bill OO, only fetch FDOC documents that were uploaded as Bill type
        // Do not fetch from external Bill API
        let fdocBillsCount = 0;
        try {
          console.log(`[Fdoc] Fetching FDOC documents uploaded as Bill type for Bill OO (site filter: ${siteId || 'none'})...`);
          const fdocParams = { limit: 500 };
          if (esignStatus) fdocParams.esign_status = esignStatus;
          if (siteId) fdocParams.site_id = siteId;
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          
          const fdocBatchSize = 10;
          const fdocDocs = [];
          
          for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
            const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc.extracted_data;
                  let docType = extractedData?.type || 'UNKNOWN';
                  
                  if (docType === 'UNKNOWN' || !docType) {
                    const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                    const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                    const combinedText = `${fileName} ${fileKey}`;
                    
                    if (combinedText.includes('bill') || combinedText.includes('receipt')) {
                      docType = 'Bill';
                    }
                  }
                  
                  const normalizedType = docType.toLowerCase();
                  console.log(`[Fdoc] Processing FDOC document ${doc.fdoc_id}, extracted type: ${docType}, normalized: ${normalizedType}`);
                  
                  if (normalizedType === 'bill' || normalizedType === 'bills' || normalizedType === 'bill/receipt') {
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    
                    console.log(`[Fdoc] Document ${doc.fdoc_id} matches Bill type. Site ID: ${docSiteId}, Filter site: ${siteId}`);
                    
                    if (siteId) {
                      if (!docSiteId || docSiteId !== siteId) {
                        console.log(`[Fdoc] Document ${doc.fdoc_id} filtered out due to site mismatch`);
                        return null;
                      }
                    }
                    
                    if (startDate || endDate) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          console.log(`[Fdoc] Document ${doc.fdoc_id} filtered out due to date before start`);
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          console.log(`[Fdoc] Document ${doc.fdoc_id} filtered out due to date after end`);
                          return null;
                        }
                      }
                    }
                    
                    const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    const uploadedDate = doc.uploaded_at 
                      ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0];
                    
                    const billDoc = {
                      id: doc.fdoc_id,
                      fdoc_id: doc.fdoc_id,
                      docNumber,
                      date: uploadedDate,
                      totalAmount: extractedData?.fields?.grand_total || extractedData?.fields?.total_amount || 0,
                      taxAmount: extractedData?.fields?.tax_total || extractedData?.fields?.tax_amount || 0,
                      status: 'Active',
                      apiStatus: doc.status || 'extracted',
                      description: `Bill OO - ${docNumber}`,
                      file_name: doc.file_name,
                      file_type: doc.file_type,
                      extracted_data: extractedData,
                      documentType: 'Bill OO',
                      siteId: docSiteId,
                      externalDoc: false,
                      originalDoc: fullDoc,
                    };
                    
                    console.log(`[Fdoc] Created Bill OO document:`, { id: billDoc.id, docNumber: billDoc.docNumber, documentType: billDoc.documentType });
                    return billDoc;
                  }
                  console.log(`[Fdoc] Document ${doc.fdoc_id} does not match Bill type (normalized: ${normalizedType})`);
                  return null;
                } catch (err) {
                  const isFileMissing = err.status === 410 || err.fileMissing === true;
                  if (isFileMissing) {
                    const dt = (doc.document_type || doc.documentType || '').toLowerCase();
                    const fn = (doc.file_name || '').toLowerCase();
                    if (!dt || dt === 'bill' || dt === 'bills' || fn.includes('bill') || fn.includes('receipt')) {
                      const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      const uploadedDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                      return {
                        id: doc.fdoc_id,
                        fdoc_id: doc.fdoc_id,
                        docNumber,
                        date: uploadedDate,
                        totalAmount: 0,
                        taxAmount: 0,
                        status: 'Failed',
                        apiStatus: doc.status || 'extraction_failed',
                        description: `Bill OO - ${docNumber}`,
                        file_name: doc.file_name,
                        file_type: doc.file_type,
                        extracted_data: null,
                        documentType: 'Bill OO',
                        siteId: doc.site_id || doc.siteId || null,
                        externalDoc: false,
                        fileMissing: true,
                      };
                    }
                  }
                  console.warn(`[Fdoc] Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                fdocDocs.push(result.value);
              }
            });
          }
          
          fdocBillsCount = fdocDocs.length;
          if (fdocDocs.length > 0) {
            console.log(`[Fdoc] Found ${fdocBillsCount} FDOC documents uploaded as Bill type`);
            documents.push(...fdocDocs);
          }
        } catch (fdocErr) {
          console.warn('[Fdoc] Error fetching FDOC documents for Bill OO:', fdocErr);
        }
        
        console.log(`[Fdoc] Total Bill OO documents: ${documents.length} (${fdocBillsCount} from FDOC only)`);
      } else if (docType === 'Invoice OO') {
        // Fetch from invoice API
        const response = await invoiceApi.listInvoices(commonParams);
        console.log(`[Fdoc] Invoice API response:`, response);
        
        let apiInvoicesCount = 0;
        if (response.success && response.invoices && Array.isArray(response.invoices)) {
          console.log(`[Fdoc] Found ${response.invoices.length} invoices from API`);
          const apiDocs = response.invoices.map(invoice => 
            transformExternalDoc(invoice, 'Invoice OO', 'invoice_id', 'invoice_number', 'invoice_date')
          );
          documents.push(...apiDocs);
          apiInvoicesCount = apiDocs.length;
        } else {
          console.warn('[Fdoc] Invoice API: Failed to fetch invoices or invalid response:', {
            success: response.success,
            hasInvoices: !!response.invoices,
            isArray: Array.isArray(response.invoices),
            error: response.error,
            response: response
          });
        }
        
        // Also fetch FDOC documents that were uploaded as Invoice type
        let fdocInvoicesCount = 0;
        try {
          console.log(`[Fdoc] Also fetching FDOC documents uploaded as Invoice type (site filter: ${siteId || 'none'})...`);
          const fdocParams = { limit: 500 };
          if (esignStatus) fdocParams.esign_status = esignStatus;
          if (siteId) fdocParams.site_id = siteId;
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          
          // Process FDOC documents to find Invoice types
          const fdocBatchSize = 10;
          const fdocDocs = [];
          
          for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
            const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc.extracted_data;
                  let docType = extractedData?.type || 'UNKNOWN';
                  
                  // Infer type from file name if needed
                  if (docType === 'UNKNOWN' || !docType) {
                    const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                    const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                    const combinedText = `${fileName} ${fileKey}`;
                    
                    if (combinedText.includes('invoice') || combinedText.includes('inv')) {
                      docType = 'Invoice';
                    }
                  }
                  
                  // Normalize Invoice types
                  const normalizedType = docType.toLowerCase();
                  if (normalizedType === 'invoice' || normalizedType === 'invoices') {
                    // Get site_id from document
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    
                    // Check site_id match if site filter is applied
                    if (siteId) {
                      if (!docSiteId || docSiteId !== siteId) {
                        console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: site mismatch`, {
                          docSiteId,
                          selectedSiteId: siteId,
                          matches: docSiteId === siteId
                        });
                        return null; // Skip if site doesn't match or document has no site_id
                      }
                    }
                    
                    // Check date range if provided
                    if (startDate || endDate) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: before start date`, { docDate, startDate });
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: after end date`, { docDate, endDate });
                          return null;
                        }
                      }
                    }
                    
                    console.log(`[Fdoc] Including FDOC document ${doc.fdoc_id} as Invoice OO`, {
                      docSiteId,
                      selectedSiteId: siteId,
                      docType,
                      fileName: fullDoc?.file_name || doc?.file_name
                    });
                    
                    // Transform to match external doc format
                    const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    const uploadedDate = doc.uploaded_at 
                      ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0];
                    
                    return {
                      id: doc.fdoc_id,
                      fdoc_id: doc.fdoc_id,
                      docNumber,
                      date: uploadedDate,
                      totalAmount: extractedData?.fields?.grand_total || extractedData?.fields?.total_amount || 0,
                      taxAmount: extractedData?.fields?.tax_total || extractedData?.fields?.tax_amount || 0,
                      status: 'Active',
                      apiStatus: doc.status || 'extracted',
                      description: `Invoice OO - ${docNumber}`,
                      file_name: doc.file_name,
                      file_type: doc.file_type,
                      extracted_data: extractedData,
                      documentType: 'Invoice OO',
                      siteId: docSiteId,
                      externalDoc: false, // This is from FDOC, not external API
                      originalDoc: fullDoc,
                    };
                  }
                  return null; // Not an Invoice document
                } catch (err) {
                  const isFileMissing = err.status === 410 || err.fileMissing === true;
                  if (isFileMissing) {
                    const dt = (doc.document_type || doc.documentType || '').toLowerCase();
                    const fn = (doc.file_name || '').toLowerCase();
                    if (!dt || dt === 'invoice' || dt === 'invoices' || fn.includes('invoice') || fn.includes('inv')) {
                      const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      const uploadedDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                      return {
                        id: doc.fdoc_id,
                        fdoc_id: doc.fdoc_id,
                        docNumber,
                        date: uploadedDate,
                        totalAmount: 0,
                        taxAmount: 0,
                        status: 'Failed',
                        apiStatus: doc.status || 'extraction_failed',
                        description: `Invoice OO - ${docNumber}`,
                        file_name: doc.file_name,
                        file_type: doc.file_type,
                        extracted_data: null,
                        documentType: 'Invoice OO',
                        siteId: doc.site_id || doc.siteId || null,
                        externalDoc: false,
                        fileMissing: true,
                      };
                    }
                  }
                  console.warn(`[Fdoc] Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                fdocDocs.push(result.value);
              }
            });
          }
          
          fdocInvoicesCount = fdocDocs.length;
          if (fdocDocs.length > 0) {
            console.log(`[Fdoc] Found ${fdocInvoicesCount} FDOC documents uploaded as Invoice type (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
            documents.push(...fdocDocs);
          } else {
            console.log(`[Fdoc] No FDOC documents found matching Invoice type with filters (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
          }
        } catch (fdocErr) {
          console.warn('[Fdoc] Error fetching FDOC documents for Invoice OO:', fdocErr);
          // Continue even if FDOC fetch fails
        }
        
        console.log(`[Fdoc] Total Invoice OO documents: ${documents.length} (${apiInvoicesCount} from API + ${fdocInvoicesCount} from FDOC)`);
      } else if (docType === 'Estimation OO') {
        // Fetch from quotation API
        const response = await quotationApi.listQuotations(commonParams);
        console.log(`[Fdoc] Quotation API response:`, response);
        
        // Handle different response structures
        let quotations = [];
        let apiQuotationsCount = 0;
        if (response.success) {
          if (Array.isArray(response.items)) {
            quotations = response.items;
          } else if (response.data && Array.isArray(response.data.items)) {
            quotations = response.data.items;
          } else if (Array.isArray(response.data)) {
            quotations = response.data;
          }
        }
        
        if (quotations.length > 0) {
          console.log(`[Fdoc] Found ${quotations.length} quotations from API (site filter: ${siteId || 'none'})`);
          
          // Transform quotations and apply client-side site filtering as fallback
          const transformedQuotations = quotations.map(quote => 
            transformExternalDoc(quote, 'Estimation OO', 'quote_id', 'quotation_number', 'issued_date')
          );
          
          // Apply site filtering if site is selected (client-side fallback)
          let filteredQuotations = transformedQuotations;
          if (siteId) {
            filteredQuotations = transformedQuotations.filter(quote => {
              const quoteSiteId = quote.originalDoc?.site_id || quote.originalDoc?.siteId || null;
              if (!quoteSiteId || quoteSiteId !== siteId) {
                console.log(`[Fdoc] Skipping quotation ${quote.id}: site mismatch`, {
                  quoteSiteId,
                  selectedSiteId: siteId,
                  matches: quoteSiteId === siteId
                });
                return false;
              }
              return true;
            });
            console.log(`[Fdoc] Filtered ${filteredQuotations.length} quotations matching site ${siteId} from ${transformedQuotations.length} total`);
          }
          
          documents.push(...filteredQuotations);
          apiQuotationsCount = filteredQuotations.length;
        } else {
          console.warn('[Fdoc] Quotation API: Failed to fetch quotations or invalid response:', {
            success: response.success,
            hasItems: !!response.items,
            hasDataItems: !!(response.data && response.data.items),
            isArray: Array.isArray(response.items),
            error: response.error,
            response: response
          });
        }
        
        // Also fetch FDOC documents that were uploaded as Estimation/Quotation type
        let fdocQuotationsCount = 0;
        try {
          console.log(`[Fdoc] Also fetching FDOC documents uploaded as Estimation type (site filter: ${siteId || 'none'})...`);
          const fdocParams = { limit: 500 };
          if (esignStatus) fdocParams.esign_status = esignStatus;
          if (siteId) fdocParams.site_id = siteId;
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          
          const fdocBatchSize = 10;
          const fdocDocs = [];
          
          for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
            const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc.extracted_data;
                  let docType = extractedData?.type || 'UNKNOWN';
                  
                  if (docType === 'UNKNOWN' || !docType) {
                    const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                    const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                    const combinedText = `${fileName} ${fileKey}`;
                    
                    if (combinedText.includes('quotation') || combinedText.includes('quote') || combinedText.includes('estimation') || combinedText.includes('estimate')) {
                      docType = 'Estimation';
                    }
                  }
                  
                  const normalizedType = docType.toLowerCase();
                  if (normalizedType === 'estimation' || normalizedType === 'estimation/quotation' || normalizedType === 'quotation' || normalizedType === 'quotations') {
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    
                    // Check site_id match if site filter is applied
                    if (siteId) {
                      if (!docSiteId || docSiteId !== siteId) {
                        console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: site mismatch`, {
                          docSiteId,
                          selectedSiteId: siteId,
                          matches: docSiteId === siteId
                        });
                        return null; // Skip if site doesn't match or document has no site_id
                      }
                    }
                    
                    // Check date range if provided
                    if (startDate || endDate) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: before start date`, { docDate, startDate });
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: after end date`, { docDate, endDate });
                          return null;
                        }
                      }
                    }
                    
                    console.log(`[Fdoc] Including FDOC document ${doc.fdoc_id} as Estimation OO`, {
                      docSiteId,
                      selectedSiteId: siteId,
                      docType,
                      fileName: fullDoc?.file_name || doc?.file_name
                    });
                    
                    const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    const uploadedDate = doc.uploaded_at 
                      ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0];
                    
                    return {
                      id: doc.fdoc_id,
                      fdoc_id: doc.fdoc_id,
                      docNumber,
                      date: uploadedDate,
                      totalAmount: extractedData?.fields?.grand_total || extractedData?.fields?.total_amount || 0,
                      taxAmount: extractedData?.fields?.tax_total || extractedData?.fields?.tax_amount || 0,
                      status: 'Active',
                      apiStatus: doc.status || 'extracted',
                      description: `Estimation OO - ${docNumber}`,
                      file_name: doc.file_name,
                      file_type: doc.file_type,
                      extracted_data: extractedData,
                      documentType: 'Estimation OO',
                      siteId: docSiteId,
                      externalDoc: false,
                      originalDoc: fullDoc,
                    };
                  }
                  return null;
                } catch (err) {
                  const isFileMissing = err.status === 410 || err.fileMissing === true;
                  if (isFileMissing) {
                    const dt = (doc.document_type || doc.documentType || '').toLowerCase();
                    const fn = (doc.file_name || '').toLowerCase();
                    if (!dt || dt === 'estimation' || dt === 'quotation' || fn.includes('quotation') || fn.includes('quote') || fn.includes('estimation')) {
                      const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      const uploadedDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                      return {
                        id: doc.fdoc_id,
                        fdoc_id: doc.fdoc_id,
                        docNumber,
                        date: uploadedDate,
                        totalAmount: 0,
                        taxAmount: 0,
                        status: 'Failed',
                        apiStatus: doc.status || 'extraction_failed',
                        description: `Estimation OO - ${docNumber}`,
                        file_name: doc.file_name,
                        file_type: doc.file_type,
                        extracted_data: null,
                        documentType: 'Estimation OO',
                        siteId: doc.site_id || doc.siteId || null,
                        externalDoc: false,
                        fileMissing: true,
                      };
                    }
                  }
                  console.warn(`[Fdoc] Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                fdocDocs.push(result.value);
              }
            });
          }
          
          fdocQuotationsCount = fdocDocs.length;
          if (fdocDocs.length > 0) {
            console.log(`[Fdoc] Found ${fdocQuotationsCount} FDOC documents uploaded as Estimation type (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
            documents.push(...fdocDocs);
          } else {
            console.log(`[Fdoc] No FDOC documents found matching Estimation type with filters (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
          }
        } catch (fdocErr) {
          console.warn('[Fdoc] Error fetching FDOC documents for Estimation OO:', fdocErr);
          // Continue even if FDOC fetch fails
        }
        
        console.log(`[Fdoc] Total Estimation OO documents: ${documents.length} (${apiQuotationsCount} from API + ${fdocQuotationsCount} from FDOC)`);
      } else if (docType === 'Purchase Order OO') {
        // For Purchase Order OO, only fetch FDOC documents that were uploaded as PO type
        // Do not fetch from external Purchase Order API. Pass document_type so backend filters.
        let fdocDocsCount = 0;
        try {
          console.log(`[Fdoc] Also fetching FDOC documents uploaded as PO type (site filter: ${siteId || 'none'})...`);
          const fdocParams = { limit: 500, document_type: 'Purchase Order' };
          if (esignStatus) fdocParams.esign_status = esignStatus;
          if (siteId) fdocParams.site_id = siteId;
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          
          // Process FDOC documents to find PO types
          const fdocBatchSize = 10;
          const fdocDocs = [];
          
          // If backend did not filter by document_type, optionally skip list items that clearly are not PO (when document_type is present on item)
          const isPoUploadType = (d) => {
            const dt = (d.document_type || d.documentType || '').toLowerCase();
            return !dt || dt === 'po' || dt === 'purchase order' || dt === 'purchase orders';
          };
          const itemsToProcess = fdocResponse.items.filter((d) => isPoUploadType(d));

          for (let i = 0; i < itemsToProcess.length; i += fdocBatchSize) {
            const batch = itemsToProcess.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc?.extracted_data;
                  // Use document_type from list/fullDoc when extraction_failed or type missing
                  let docType = fullDoc?.document_type || doc.document_type || extractedData?.type || 'UNKNOWN';
                  
                  // Infer type from file name if needed (e.g. when extraction_failed)
                  if (docType === 'UNKNOWN' || !docType) {
                    const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                    const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                    const combinedText = `${fileName} ${fileKey}`;
                    
                    if (combinedText.includes('po') || combinedText.includes('purchase') || combinedText.includes('purchase order')) {
                      docType = 'Purchase Order';
                    }
                  }
                  
                  // Normalize PO types (include 'purchase order' as stored by backend)
                  const normalizedType = (docType || '').toLowerCase();
                  if (normalizedType === 'po' || normalizedType === 'purchase order' || normalizedType === 'purchase orders') {
                    // Get site_id from document
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    
                    // Check site_id match if site filter is applied
                    // If site is selected, only include documents that match that site
                    // Exclude documents without site_id when a site is selected
                    if (siteId) {
                      if (!docSiteId || docSiteId !== siteId) {
                        console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: site mismatch`, {
                          docSiteId,
                          selectedSiteId: siteId,
                          matches: docSiteId === siteId
                        });
                        return null; // Skip if site doesn't match or document has no site_id
                      }
                    }
                    
                    // Check date range if provided
                    if (startDate || endDate) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: before start date`, { docDate, startDate });
                          return null;
                        }
                        if (endDate && docDate > endDate) {
                          console.log(`[Fdoc] Skipping FDOC document ${doc.fdoc_id}: after end date`, { docDate, endDate });
                          return null;
                        }
                      }
                    }
                    
                    console.log(`[Fdoc] Including FDOC document ${doc.fdoc_id} as Purchase Order OO`, {
                      docSiteId,
                      selectedSiteId: siteId,
                      docType,
                      fileName: fullDoc?.file_name || doc?.file_name
                    });
                    
                    // Transform to match external doc format
                    const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    const uploadedDate = doc.uploaded_at 
                      ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0];
                    
                    return {
                      id: doc.fdoc_id,
                      fdoc_id: doc.fdoc_id,
                      docNumber,
                      date: uploadedDate,
                      totalAmount: extractedData?.fields?.grand_total || extractedData?.fields?.total_amount || 0,
                      taxAmount: extractedData?.fields?.tax_total || extractedData?.fields?.tax_amount || 0,
                      status: 'Active',
                      apiStatus: doc.status || 'extracted',
                      description: `Purchase Order OO - ${docNumber}`,
                      file_name: doc.file_name,
                      file_type: doc.file_type,
                      extracted_data: extractedData,
                      documentType: 'Purchase Order OO',
                      siteId: docSiteId,
                      externalDoc: false, // This is from FDOC, not external API
                      originalDoc: fullDoc,
                    };
                  }
                  return null; // Not a PO document
                } catch (err) {
                  const isFileMissing = err.status === 410 || err.fileMissing === true;
                  if (isFileMissing) {
                    const dt = (doc.document_type || doc.documentType || '').toLowerCase();
                    const fn = (doc.file_name || '').toLowerCase();
                    if (!dt || dt === 'po' || dt === 'purchase order' || fn.includes('po') || fn.includes('purchase')) {
                      const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                      const uploadedDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                      return {
                        id: doc.fdoc_id,
                        fdoc_id: doc.fdoc_id,
                        docNumber,
                        date: uploadedDate,
                        totalAmount: 0,
                        taxAmount: 0,
                        status: 'Failed',
                        apiStatus: doc.status || 'extraction_failed',
                        description: `Purchase Order OO - ${docNumber}`,
                        file_name: doc.file_name,
                        file_type: doc.file_type,
                        extracted_data: null,
                        documentType: 'Purchase Order OO',
                        siteId: doc.site_id || doc.siteId || null,
                        externalDoc: false,
                        fileMissing: true,
                      };
                    }
                  }
                  console.warn(`[Fdoc] Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                fdocDocs.push(result.value);
              }
            });
          }
          
          fdocDocsCount = fdocDocs.length;
          if (fdocDocs.length > 0) {
            console.log(`[Fdoc] Found ${fdocDocsCount} FDOC documents uploaded as PO type (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
            documents.push(...fdocDocs);
          } else {
            console.log(`[Fdoc] No FDOC documents found matching PO type with filters (site: ${siteId || 'all'}, date range: ${startDate || 'any'} to ${endDate || 'any'})`);
          }
        } catch (fdocErr) {
          console.warn('[Fdoc] Error fetching FDOC documents for Purchase Order OO:', fdocErr);
          // Continue even if FDOC fetch fails
        }
        
        console.log(`[Fdoc] Total Purchase Order OO documents: ${documents.length} (${fdocDocsCount} from FDOC only)`);
      } else if (docType === 'Amenity Booking Invoice') {
        // FDOC API returns: fdoc_id, file_name, file_type, extracted_data.type, uploaded_at, status. No document_type/site_id yet.
        // Match: (1) document_type/type === "Amenity Booking Invoice" when API returns it,
        // (2) extracted type "Amenity Booking Invoice", (3) extracted "Invoice" + amenity/facility/booking in filename,
        // (4) fallback until API returns document_type: extracted "Invoice"/"PO"/"UNKNOWN" and filename contains "invoice".
        let fdocAmenityCount = 0;
        try {
          console.log(`[Fdoc] Fetching FDOC documents for Amenity Booking Invoice (site filter: ${siteId || 'none'})...`);
          const fdocParams = { limit: 500 };
          if (esignStatus) fdocParams.esign_status = esignStatus;
          if (siteId) fdocParams.site_id = siteId;
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          const fdocBatchSize = 10;
          const fdocDocs = [];
          for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
            const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc.extracted_data;
                  const uploadType = fullDoc?.document_type || fullDoc?.type || fullDoc?.metadata?.document_type || null;
                  let docTypeVal = uploadType || (extractedData?.type ?? 'UNKNOWN');
                  const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                  const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                  const combined = `${fileName} ${fileKey}`;
                  const filenameHasInvoice = combined.includes('invoice');
                  const filenameSuggestsAmenity = (combined.includes('amenity') || combined.includes('facility') || combined.includes('booking')) && filenameHasInvoice;
                  if (docTypeVal === 'UNKNOWN' || !docTypeVal) {
                    if (filenameSuggestsAmenity) {
                      docTypeVal = 'Amenity Booking Invoice';
                    }
                  }
                  const normalized = (docTypeVal || '').toLowerCase();
                  const isExplicitAmenity = normalized === 'amenity booking invoice';
                  const isInvoiceWithAmenityFilename = normalized === 'invoice' && filenameSuggestsAmenity;
                  const isFallbackInvoiceOrPo = (normalized === 'invoice' || normalized === 'po' || normalized === 'unknown') && filenameHasInvoice;
                  const isAmenityType = isExplicitAmenity || isInvoiceWithAmenityFilename || isFallbackInvoiceOrPo;
                  if (isAmenityType) {
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    // Only exclude when doc has a site that does not match; do not exclude docs with no site_id
                    if (siteId && docSiteId && docSiteId !== siteId) return null;
                    if (startDate || endDate) {
                      const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                      if (docDate) {
                        if (startDate && docDate < startDate) return null;
                        if (endDate && docDate > endDate) return null;
                      }
                    }
                    const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    const uploadedDate = doc.uploaded_at
                      ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                      : new Date().toISOString().split('T')[0];
                    return {
                      id: doc.fdoc_id,
                      fdoc_id: doc.fdoc_id,
                      docNumber,
                      date: uploadedDate,
                      totalAmount: extractedData?.fields?.grand_total || extractedData?.fields?.total_amount || 0,
                      taxAmount: extractedData?.fields?.tax_total || extractedData?.fields?.tax_amount || 0,
                      status: 'Active',
                      apiStatus: doc.status || 'extracted',
                      description: `Amenity Booking Invoice - ${docNumber}`,
                      file_name: doc.file_name,
                      file_type: doc.file_type,
                      extracted_data: extractedData,
                      documentType: 'Amenity Booking Invoice',
                      siteId: docSiteId,
                      externalDoc: false,
                      originalDoc: fullDoc,
                    };
                  }
                  return null;
                } catch (err) {
                  console.warn(`[Fdoc] Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            batchResults.forEach((r) => {
              if (r.status === 'fulfilled' && r.value) fdocDocs.push(r.value);
            });
          }
          fdocAmenityCount = fdocDocs.length;
          if (fdocDocs.length > 0) {
            documents.push(...fdocDocs);
            console.log(`[Fdoc] Found ${fdocAmenityCount} FDOC documents for Amenity Booking Invoice`);
          }
        } catch (fdocErr) {
          console.warn('[Fdoc] Error fetching FDOC documents for Amenity Booking Invoice:', fdocErr);
        }
        console.log(`[Fdoc] Total Amenity Booking Invoice documents: ${documents.length} (${fdocAmenityCount} from FDOC)`);
      }
      
      console.log(`[Fdoc] Transformed ${documents.length} ${docType} documents`);
      return documents;
    } catch (err) {
      console.error(`[Fdoc] Error fetching ${docType} documents:`, err);
      throw err; // Re-throw to be caught by the outer try-catch
    }
  };

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (selectedFdocType === FDOC_ORDER_HISTORY_TYPE) {
          const { date_from, date_to } = getMonthDateRange(selectedMonth);
          const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
          const effectiveKind =
            orderHistoryKind === 'both'
              ? orderHistoryListKind
              : orderHistoryKind === 'customer'
                ? 'customer'
                : 'vendor';
          const params = {
            kind: effectiveKind,
            date_from,
            date_to,
            status: 'confirmed,extracted',
            limit: 50,
          };
          if (siteId) params.site_id = siteId;
          if (effectiveKind === 'vendor' && selectedOrderHistoryVendorId) {
            Object.assign(params, parseVendorFilterSelection(selectedOrderHistoryVendorId));
          }
          if (effectiveKind === 'customer' && selectedOrderHistoryCustomerId) {
            Object.assign(params, parseCustomerFilterSelection(selectedOrderHistoryCustomerId));
          }
          const response = await fdocApi.getOrderHistory(params);
          setOrderHistorySummary(
            response.summary || { total_amount_sum: 0, tax_total_sum: 0 }
          );
          setOrderHistoryLastKey(response.last_key ?? null);
          const transformed = (response.entries || []).map(transformOrderHistoryEntry);
          setDocuments(transformed);
          setIsLoading(false);
          return;
        }

        // Check if this is an "OO" type or FDOC-upload-only type (e.g. Amenity Booking Invoice)
        const isOOType = selectedFdocType && selectedFdocType.includes('OO');
        const isFdocUploadType = selectedFdocType === 'Amenity Booking Invoice';
        
        if (isOOType || isFdocUploadType) {
          // Fetch from external APIs or FDOC with site and date filtering
          console.log(`[Fdoc] Fetching type: ${selectedFdocType}`);
          
          // Calculate date range from selected month
          const [year, month] = selectedMonth.split('-');
          const startDate = `${year}-${month}-01`;
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
          
          // Get site_id if site is selected
          const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
          
          const externalDocs = await fetchExternalDocuments(selectedFdocType, siteId, startDate, endDate, esignedOnly ? 'applied' : undefined);
          console.log(`[Fdoc] Received ${externalDocs.length} documents for ${selectedFdocType} (site: ${siteId}, month: ${selectedMonth})`);
          console.log(`[Fdoc] Document details:`, externalDocs.map(d => ({ 
            id: d.id, 
            docNumber: d.docNumber, 
            documentType: d.documentType,
            siteId: d.siteId 
          })));
          setDocuments(externalDocs);
          setIsLoading(false);
          return;
        }

        // Check if this is a direct API type (Bill, Invoice, Purchase Order, Estimation - non-OO)
        // These should fetch from their respective external APIs, not FDOC
        const isDirectApiType = ['Bill', 'Invoice', 'Purchase Order', 'Estimation'].includes(selectedFdocType);
        
        if (isDirectApiType) {
          // Fetch from external APIs for non-OO types
          console.log(`[Fdoc] Fetching ${selectedFdocType} from external API...`);
          
          // Calculate date range from selected month
          const [year, month] = selectedMonth.split('-');
          const startDate = `${year}-${month}-01`;
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
          
          // Get site_id if site is selected
          const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
          
          let apiDocs = [];
          
          if (selectedFdocType === 'Bill') {
            // Fetch from Bill API
            const billParams = {
              limit: 500,
            };
            if (siteId) billParams.site_id = siteId;
            if (startDate) billParams.start_date = startDate;
            if (endDate) billParams.end_date = endDate;
            
            const billResponse = await billApi.listBills(billParams);
            if (billResponse.success && billResponse.bills && Array.isArray(billResponse.bills)) {
              apiDocs = billResponse.bills.map(bill => {
                console.log('[Fdoc] Raw Bill from API:', {
                  bill_id: bill.bill_id,
                  bill_number: bill.bill_number,
                  order_id: bill.order_id,
                  table_number: bill.table_number,
                  site_id: bill.site_id,
                  site_name: bill.site_name,
                  customer: bill.customer,
                  hasLineItems: !!(bill.line_items && bill.line_items.length > 0),
                  lineItemsCount: bill.line_items?.length || 0,
                  subtotal: bill.subtotal,
                  total: bill.total,
                  billKeys: Object.keys(bill)
                });
                const transformed = transformExternalDoc(bill, 'Bill', 'bill_id', 'bill_number', 'created_at');
                console.log('[Fdoc] Transformed Bill:', {
                  id: transformed.id,
                  billNumber: transformed.docNumber,
                  hasOriginalDoc: !!transformed.originalDoc,
                  originalDocKeys: transformed.originalDoc ? Object.keys(transformed.originalDoc) : [],
                  originalDocBillNumber: transformed.originalDoc?.bill_number,
                  originalDocOrderId: transformed.originalDoc?.order_id,
                  originalDocTableNumber: transformed.originalDoc?.table_number,
                  originalDocSiteId: transformed.originalDoc?.site_id,
                  originalDocCustomer: transformed.originalDoc?.customer,
                  originalDocLineItems: transformed.originalDoc?.line_items?.length || 0,
                  externalDoc: transformed.externalDoc
                });
                return transformed;
              });
              console.log(`[Fdoc] Fetched ${apiDocs.length} bills from Bill API`);
            }
          } else if (selectedFdocType === 'Invoice') {
            // Fetch from Invoice API
            const invoiceParams = {
              limit: 500,
            };
            if (siteId) invoiceParams.site_id = siteId;
            if (startDate) invoiceParams.start_date = startDate;
            if (endDate) invoiceParams.end_date = endDate;
            
            const invoiceResponse = await invoiceApi.listInvoices(invoiceParams);
            if (invoiceResponse.success && invoiceResponse.invoices && Array.isArray(invoiceResponse.invoices)) {
              apiDocs = invoiceResponse.invoices.map(invoice => 
                transformExternalDoc(invoice, 'Invoice', 'invoice_id', 'invoice_number', 'invoice_date')
              );
              console.log(`[Fdoc] Fetched ${apiDocs.length} invoices from Invoice API`);
            }
          } else if (selectedFdocType === 'Purchase Order') {
            // Fetch from Purchase Order API
            const poParams = {
              limit: 500,
            };
            if (siteId) poParams.site_id = siteId;
            if (startDate) poParams.start_date = startDate;
            if (endDate) poParams.end_date = endDate;
            
            const poResponse = await purchaseOrderApi.getPurchaseOrders(poParams);
            if (poResponse.success && poResponse.orders && Array.isArray(poResponse.orders)) {
              apiDocs = poResponse.orders.map(po => 
                transformExternalDoc(po, 'Purchase Order', 'po_id', 'po_number', 'po_date')
              );
              console.log(`[Fdoc] Fetched ${apiDocs.length} purchase orders from PO API`);
            }
          } else if (selectedFdocType === 'Estimation') {
            // Fetch from Quotation API
            const quoteParams = {
              limit: 500,
            };
            if (siteId) quoteParams.site_id = siteId;
            if (startDate) quoteParams.start_date = startDate;
            if (endDate) quoteParams.end_date = endDate;
            
            const quoteResponse = await quotationApi.listQuotations(quoteParams);
            let quotations = [];
            if (quoteResponse.success) {
              if (Array.isArray(quoteResponse.items)) {
                quotations = quoteResponse.items;
              } else if (quoteResponse.data && Array.isArray(quoteResponse.data.items)) {
                quotations = quoteResponse.data.items;
              } else if (Array.isArray(quoteResponse.data)) {
                quotations = quoteResponse.data;
              }
            }
            if (quotations.length > 0) {
              apiDocs = quotations.map(quote => 
                transformExternalDoc(quote, 'Estimation', 'quote_id', 'quotation_number', 'issued_date')
              );
              console.log(`[Fdoc] Fetched ${apiDocs.length} quotations from Quotation API`);
            }
          }
          
          setDocuments(apiDocs);
          setIsLoading(false);
          return;
        }

        // Otherwise, fetch from FDOC API (for UNKNOWN or other types)
        // Build API parameters - fetch all documents (no status filter)
        const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
        const params = {
          limit: 500, // Get up to 500 documents
        };
        if (siteId) params.site_id = siteId;
        if (esignedOnly) params.esign_status = 'applied';

        // Fetch all documents (we'll filter by document type and site client-side)
        const response = await fdocApi.listDocuments(params);
        
        // Log successful API call
        loggingService.logInfo(
          'fdocListDocuments',
          'Successfully fetched Fdoc documents list',
          {
            endpoint: '/fdoc',
            method: 'GET',
            documentType: selectedFdocType,
            siteId: selectedSite ? resolveSiteValue(selectedSite) : null,
            month: selectedMonth,
            documentCount: response.count || 0,
            itemsCount: response.items?.length || 0,
          }
        ).catch(logError => {
          console.warn('[Fdoc] Failed to log event:', logError);
        });
        
        // Transform API response to component format
        // Process documents in batches to avoid overwhelming the server
        const batchSize = 10; // Process 10 documents at a time
        const transformedDocuments = [];
        
        for (let i = 0; i < response.items.length; i += batchSize) {
          const batch = response.items.slice(i, i + batchSize);
          const batchResults = await Promise.allSettled(
            batch.map(async (doc) => {
              // Try to get full document details to access extracted_data
              let fullDoc = null;
              let extractedData = null;
              let totalAmount = 0;
              let taxAmount = 0;
              let docType = 'UNKNOWN';
              let fileMissing = false;

              try {
                fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                
                // Log successful document fetch
                loggingService.logInfo(
                  'fdocGetDocument',
                  'Successfully fetched Fdoc document details',
                  {
                    endpoint: `/fdoc/${doc.fdoc_id}`,
                    method: 'GET',
                    fdocId: doc.fdoc_id,
                    hasExtractedData: !!fullDoc.extracted_data,
                  }
                ).catch(logError => {
                  console.warn('[Fdoc] Failed to log event:', logError);
                });
                
                extractedData = fullDoc.extracted_data;
                // Detect file_missing from response body (e.g. API returns 200 with error in body)
                const respBody = fullDoc?.body || fullDoc;
                if (fullDoc && (fullDoc.statusCode === 410 || fullDoc.error === 'file_missing' || respBody?.error === 'file_missing')) {
                  fileMissing = true;
                }
                if (fileMissing) {
                  await autoCancelMissingFdoc(doc.fdoc_id, 'file missing (410), auto-deleted from FDOC list');
                  return null;
                }
                if (extractedData) {
                  docType = extractedData.type || 'UNKNOWN';
                  
                  // Try to extract amounts from fields
                  if (extractedData.fields) {
                    // For invoices/bills, look for total_amount or similar
                    if (extractedData.fields.total_amount) {
                      totalAmount = parseFloat(extractedData.fields.total_amount) || 0;
                    } else if (extractedData.fields.grand_total) {
                      totalAmount = parseFloat(extractedData.fields.grand_total) || 0;
                    }
                    
                    // Look for tax amounts
                    if (extractedData.fields.tax_total) {
                      taxAmount = parseFloat(extractedData.fields.tax_total) || 0;
                    } else if (extractedData.fields.tax_amount) {
                      taxAmount = parseFloat(extractedData.fields.tax_amount) || 0;
                    } else if (extractedData.fields.cgst && extractedData.fields.sgst) {
                      taxAmount = 
                        (parseFloat(extractedData.fields.cgst) || 0) +
                        (parseFloat(extractedData.fields.sgst) || 0);
                    }
                  }
                }
                
                // If type is still UNKNOWN, try to infer from file name or other metadata
                if (docType === 'UNKNOWN' || !docType) {
                  const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                  const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                  const combinedText = `${fileName} ${fileKey}`;
                  
                  // Check for purchase order indicators
                  if (combinedText.includes('po') || combinedText.includes('purchase') || combinedText.includes('purchase order')) {
                    docType = 'Purchase Order';
                  }
                  // Check for invoice indicators
                  else if (combinedText.includes('invoice') || combinedText.includes('inv')) {
                    docType = 'Invoice';
                  }
                  // Check for bill indicators
                  else if (combinedText.includes('bill') || combinedText.includes('receipt')) {
                    docType = 'Bill';
                  }
                  // Check for quotation/estimation indicators
                  else if (combinedText.includes('quotation') || combinedText.includes('quote') || combinedText.includes('estimation') || combinedText.includes('estimate')) {
                    docType = 'Estimation';
                  }
                }
              } catch (err) {
                const isFileMissing = err.status === 410 || err.fileMissing === true;
                if (isFileMissing) {
                  await autoCancelMissingFdoc(doc.fdoc_id, 'file missing (410), auto-deleted from FDOC list');
                  return null;
                } else {
                  console.warn(`[Fdoc] Failed to fetch full document ${doc.fdoc_id}:`, err.message || err);
                }
                fileMissing = isFileMissing;
                // Use list item document_type when getDocument failed so type filter still works
                if (doc.document_type) docType = doc.document_type;
                // Try to infer type from file name even if fetch failed
                if (docType === 'UNKNOWN' || !docType) {
                  const fileName = (doc?.file_name || '').toLowerCase();
                  if (fileName.includes('po') || fileName.includes('purchase')) {
                    docType = 'Purchase Order';
                  } else if (fileName.includes('invoice') || fileName.includes('inv')) {
                    docType = 'Invoice';
                  } else if (fileName.includes('bill') || fileName.includes('receipt')) {
                    docType = 'Bill';
                  } else if (fileName.includes('quotation') || fileName.includes('quote') || fileName.includes('estimation')) {
                    docType = 'Estimation';
                  }
                }
              }

              // Get site_id from document (could be in doc or fullDoc)
              const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;

              // Format document number from fdoc_id
              const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
              
              // Parse uploaded date
              const uploadedDate = doc.uploaded_at 
                ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

              // Map status to display format
              const statusMap = {
                uploaded: 'Pending',
                extracting: 'Processing',
                extracted: 'Active',
                extraction_failed: 'Failed',
                confirmed: 'Completed',
              };
              const displayStatus = statusMap[doc.status] || doc.status;

              // Log document info for debugging
              if (selectedFdocType === 'Purchase Order') {
                console.log(`[Fdoc] Document ${doc.fdoc_id}:`, {
                  docType,
                  siteId: docSiteId,
                  selectedSiteId: siteId,
                  status: doc.status,
                  hasExtractedData: !!extractedData
                });
              }

              return {
                id: doc.fdoc_id,
                fdoc_id: doc.fdoc_id,
                docNumber,
                date: uploadedDate,
                version_no:
                  fullDoc?.latest_version_no ??
                  fullDoc?.version_no ??
                  fullDoc?.versionNo ??
                  doc?.latest_version_no ??
                  doc?.version_no ??
                  doc?.versionNo ??
                  null,
                versionNo:
                  fullDoc?.latest_version_no ??
                  fullDoc?.version_no ??
                  fullDoc?.versionNo ??
                  doc?.latest_version_no ??
                  doc?.version_no ??
                  doc?.versionNo ??
                  null,
                totalAmount,
                taxAmount,
                status: displayStatus,
                apiStatus: doc.status,
                description: `${docType} - ${doc.file_name || 'Document'}`,
                file_name: doc.file_name,
                file_type: doc.file_type,
                extracted_data: extractedData,
                documentType: docType, // Store document type for filtering
                siteId: docSiteId, // Store site_id for filtering
                fileMissing: false,
                // E-sign fields so "E-signed only" filter works (from fullDoc or list item)
                esign_applied: fullDoc?.esign_applied ?? doc?.esign_applied,
                esign_applied_at: fullDoc?.esign_applied_at ?? doc?.esign_applied_at,
                esign_signature: fullDoc?.esign_signature ?? doc?.esign_signature,
                originalDoc: fullDoc || doc,
              };
            })
          );
          
          // Process batch results - include successful ones; for rejected, add fallback so extraction_failed/confirmed still appear
          batchResults.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              if (result.value) transformedDocuments.push(result.value);
            } else {
              const doc = batch[index];
              console.warn(`[Fdoc] Failed to process document ${doc?.fdoc_id}:`, result.reason);
              if (doc) {
                const docNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                const uploadedDate = doc.uploaded_at 
                  ? new Date(doc.uploaded_at).toISOString().split('T')[0]
                  : new Date().toISOString().split('T')[0];
                const statusMap = {
                  uploaded: 'Pending',
                  extracting: 'Processing',
                  extracted: 'Active',
                  extraction_failed: 'Failed',
                  confirmed: 'Completed',
                };
                const reason = result.reason || {};
                const isFileMissing = reason.status === 410 || reason.fileMissing === true;
                if (isFileMissing) {
                  void autoCancelMissingFdoc(doc.fdoc_id, 'file missing (410), auto-deleted from FDOC list');
                  return;
                }
                transformedDocuments.push({
                  id: doc.fdoc_id,
                  fdoc_id: doc.fdoc_id,
                  docNumber,
                  date: uploadedDate,
                  version_no: doc?.latest_version_no ?? doc?.version_no ?? doc?.versionNo ?? null,
                  versionNo: doc?.latest_version_no ?? doc?.version_no ?? doc?.versionNo ?? null,
                  totalAmount: 0,
                  taxAmount: 0,
                  status: statusMap[doc.status] || doc.status,
                  apiStatus: doc.status,
                  description: doc.file_name ? `${doc.file_name} (document)` : 'Document',
                  file_name: doc.file_name,
                  file_type: doc.file_type,
                  extracted_data: null,
                  documentType: doc.document_type || 'UNKNOWN',
                  siteId: doc.site_id || doc.siteId || null,
                  hasError: true,
                  fileMissing: false,
                });
              }
            }
          });
        }

        // Filter documents by selected document type and site if selected
        let filteredDocuments = transformedDocuments;
        if (selectedFdocType || siteId) {
          filteredDocuments = transformedDocuments.filter((doc) => {
            // Filter by site if site is selected
            if (siteId) {
              const docSiteId = doc.siteId || doc.site_id || null;
              // Only include documents that match the selected site
              // Exclude documents from other sites or documents without site_id
              if (!docSiteId || docSiteId !== siteId) {
                return false; // Exclude documents from other sites or without site_id
              }
            }
            
            // Filter by document type if type is selected
            if (selectedFdocType) {
              const docType = doc.documentType || doc.extracted_data?.type || 'UNKNOWN';
              
              // Normalize types for matching
              // Handle variations like "PO" -> "Purchase Order"
              const normalizeType = (type) => {
                if (!type) return '';
                const normalized = type.trim();
                // Map common variations - handle case-insensitive matching
                const normalizedLower = normalized.toLowerCase();
                
                // Map PO variations to "Purchase Order"
                if (normalizedLower === 'po' || normalizedLower === 'purchase order' || normalizedLower === 'purchase orders') {
                  return 'Purchase Order';
                }
                // Map Invoice variations
                if (normalizedLower === 'invoice' || normalizedLower === 'invoices') {
                  return 'Invoice';
                }
                // Handle OO variations FIRST (Bill OO, Invoice OO, etc.) - before mapping base types
                // This ensures "Bill OO" doesn't get normalized to just "Bill"
                if (normalized.includes('OO')) {
                  return normalized; // Keep OO types as-is
                }
                // Map Bill variations (only if not OO)
                if (normalizedLower === 'bill' || normalizedLower === 'bills' || normalizedLower === 'bill/receipt') {
                  return 'Bill';
                }
                // Map Estimation/Quotation variations
                if (normalizedLower === 'estimation' || normalizedLower === 'estimation/quotation' || 
                    normalizedLower === 'quotation' || normalizedLower === 'quotations') {
                  return 'Estimation';
                }
                return normalized;
              };
              
              const normalizedDocType = normalizeType(docType);
              const normalizedSelectedType = normalizeType(selectedFdocType);
              
              // Debug logging for Purchase Order, Bill, and Bill OO type matching
              if (selectedFdocType === 'Purchase Order' || selectedFdocType === 'Purchase Order OO' ||
                  selectedFdocType === 'Bill' || selectedFdocType === 'Bill OO') {
                console.log(`[Fdoc] Checking document type match:`, {
                  docId: doc.id,
                  docType,
                  docDocumentType: doc.documentType,
                  normalizedDocType,
                  selectedFdocType,
                  normalizedSelectedType,
                  matches: normalizedDocType.toLowerCase() === normalizedSelectedType.toLowerCase(),
                  siteId: doc.siteId,
                  selectedSiteId: siteId
                });
              }
              
              // Match exact type (case-insensitive)
              if (normalizedDocType.toLowerCase() !== normalizedSelectedType.toLowerCase()) {
                return false; // Exclude documents that don't match type
              }
            }
            
            return true; // Include document if it passes all filters
          });
          
          console.log(`[Fdoc] Filtered ${filteredDocuments.length} documents for type "${selectedFdocType}" and site "${siteId}" from ${transformedDocuments.length} total documents`);
        }

        setDocuments(filteredDocuments);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError(err.message || 'Failed to load documents. Please try again.');
        setDocuments([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if a type is selected (to avoid unnecessary API calls)
    if (selectedFdocType) {
      fetchDocuments();
    } else {
      setDocuments([]);
      setIsLoading(false);
    }
  }, [
    selectedFdocType,
    selectedSite,
    selectedMonth,
    esignedOnly,
    orderHistoryKind,
    orderHistoryListKind,
    selectedOrderHistoryVendorId,
    selectedOrderHistoryCustomerId,
  ]);

  const loadMoreOrderHistory = useCallback(async () => {
    if (
      !orderHistoryLastKey ||
      selectedFdocType !== FDOC_ORDER_HISTORY_TYPE ||
      orderHistoryLoadingMore
    ) {
      return;
    }
    setOrderHistoryLoadingMore(true);
    try {
      const { date_from, date_to } = getMonthDateRange(selectedMonth);
      const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
      const effectiveKind =
        orderHistoryKind === 'both'
          ? orderHistoryListKind
          : orderHistoryKind === 'customer'
            ? 'customer'
            : 'vendor';
      const lastKeyStr =
        typeof orderHistoryLastKey === 'string'
          ? orderHistoryLastKey
          : JSON.stringify(orderHistoryLastKey);
      const params = {
        kind: effectiveKind,
        date_from,
        date_to,
        status: 'confirmed,extracted',
        limit: 50,
        last_key: lastKeyStr,
      };
      if (siteId) params.site_id = siteId;
      if (effectiveKind === 'vendor' && selectedOrderHistoryVendorId) {
        Object.assign(params, parseVendorFilterSelection(selectedOrderHistoryVendorId));
      }
      if (effectiveKind === 'customer' && selectedOrderHistoryCustomerId) {
        Object.assign(params, parseCustomerFilterSelection(selectedOrderHistoryCustomerId));
      }
      const response = await fdocApi.getOrderHistory(params);
      const newEntries = (response.entries || []).map(transformOrderHistoryEntry);
      setDocuments((prev) => [...prev, ...newEntries]);
      setOrderHistoryLastKey(response.last_key ?? null);
      if (response.summary) setOrderHistorySummary(response.summary);
    } catch (e) {
      console.warn('[Fdoc] Load more order history failed:', e?.message || e);
    } finally {
      setOrderHistoryLoadingMore(false);
    }
  }, [
    orderHistoryLastKey,
    selectedFdocType,
    orderHistoryLoadingMore,
    selectedMonth,
    selectedSite,
    orderHistoryKind,
    orderHistoryListKind,
    selectedOrderHistoryVendorId,
    selectedOrderHistoryCustomerId,
  ]);

  const effectiveOrderHistoryKind = useMemo(() => {
    if (orderHistoryKind === 'both') return orderHistoryListKind;
    return orderHistoryKind === 'customer' ? 'customer' : 'vendor';
  }, [orderHistoryKind, orderHistoryListKind]);

  // Table view is now the default for the document list (replaces card grid).
  const isCardGridView = false;


  // Filter data based on selected month (client-side fallback for non-OO documents)
  // OO documents are already filtered by site and month in the API call
  const filteredData = useMemo(() => {
    if (!selectedMonth || !selectedFdocType || documents.length === 0) {
      console.log(`[Fdoc] filteredData: Returning empty array`, { 
        selectedMonth, 
        selectedFdocType, 
        documentsLength: documents.length 
      });
      return [];
    }

    if (selectedFdocType === FDOC_ORDER_HISTORY_TYPE) {
      return documents;
    }

    // Check if this is an OO type or FDOC-upload type - if so, documents are already filtered by API
    const isOOType = selectedFdocType && selectedFdocType.includes('OO');
    const isFdocUploadType = selectedFdocType === 'Amenity Booking Invoice';
    
    if (isOOType || isFdocUploadType) {
      // OO / Amenity documents are already filtered by site and month in API, just return them
      console.log(`[Fdoc] filteredData: Returning ${documents.length} documents for ${selectedFdocType}`, 
        documents.map(d => ({ id: d.id, docNumber: d.docNumber, documentType: d.documentType }))
      );
      const isEsigned = (d) => {
        if (!d) return false;
        const o = d.originalDoc || d;
        if (o.esign_applied === true || d.esign_applied === true) return true;
        if (o.esignApplied === true || d.esignApplied === true) return true;
        if (o.esign_signature || d.esign_signature) return true;
        if (o.esignSignature || d.esignSignature) return true;
        if (o.esign_applied_at || d.esign_applied_at) return true;
        if (o.esignAppliedAt || d.esignAppliedAt) return true;
        return false;
      };
      return esignedOnly ? documents.filter(isEsigned) : documents;
    }

    // For non-OO documents, filter by month client-side
    // Extract year and month from selectedMonth (format: YYYY-MM)
    const [year, month] = selectedMonth.split('-');
    
    // Filter documents by month
    const monthFiltered = documents.filter((doc) => {
      const docDate = new Date(doc.date);
      const docYear = docDate.getFullYear();
      const docMonth = String(docDate.getMonth() + 1).padStart(2, '0');
      
      return docYear === parseInt(year) && docMonth === month;
    });
    const isEsigned = (d) => {
      if (!d) return false;
      const o = d.originalDoc || d;
      if (o.esign_applied === true || d.esign_applied === true) return true;
      if (o.esignApplied === true || d.esignApplied === true) return true;
      if (o.esign_signature || d.esign_signature) return true;
      if (o.esignSignature || d.esignSignature) return true;
      if (o.esign_applied_at || d.esign_applied_at) return true;
      if (o.esignAppliedAt || d.esignAppliedAt) return true;
      return false;
    };
    return esignedOnly ? monthFiltered.filter(isEsigned) : monthFiltered;
  }, [selectedMonth, selectedFdocType, documents, esignedOnly]);

  const searchedData = useMemo(() => filteredData, [filteredData]);

  // Fetch Purchase Order details when a card is selected
  useEffect(() => {
    const fetchPurchaseOrderDetails = async () => {
      if (!selectedPurchaseOrder) return;

      const poId = selectedPurchaseOrder.id || selectedPurchaseOrder.fdoc_id;
      if (!poId) return;

      // Skip fetch when source file is missing (410) — show re-upload message only
      if (selectedPurchaseOrder.fileMissing) {
        setIsLoadingPurchaseOrderDetails(false);
        return;
      }

      // Check if we already have the details cached
      if (purchaseOrderDetails[poId] && purchaseOrderDetails[poId].poNumber && purchaseOrderDetails[poId].poNumber !== 'N/A') {
        // Already have details, just update the selected purchase order
        setSelectedPurchaseOrder(purchaseOrderDetails[poId]);
        return;
      }
      
      // Check if selectedPurchaseOrder already has complete data (from originalDoc)
      if (selectedPurchaseOrder.originalDoc && selectedPurchaseOrder.originalDoc.extracted_data) {
        console.log('[Fdoc] Using originalDoc from selectedPurchaseOrder');
        // We'll process this in the fetch logic below
      }

      // Check if we're already fetching this PO to prevent duplicate calls
      if (fetchingPurchaseOrderRef.current) {
        console.log('[Fdoc] Already fetching Purchase Order details, skipping duplicate call');
        return;
      }

      fetchingPurchaseOrderRef.current = true;
      setIsLoadingPurchaseOrderDetails(true);
      try {
        const isExternalDoc = selectedPurchaseOrder.externalDoc === true;

        // For external documents, fetch from Purchase Order API
        if (isExternalDoc && poId) {
          // For external docs, use po_id from originalDoc if available, otherwise use the id
          const poIdToFetch = selectedPurchaseOrder.originalDoc?.po_id || 
                             selectedPurchaseOrder.originalDoc?.id || 
                             poId;
          
          console.log('[Fdoc] Fetching external Purchase Order details:', {
            selectedId: poId,
            poIdToFetch: poIdToFetch,
            hasOriginalDoc: !!selectedPurchaseOrder.originalDoc,
            originalDocPoId: selectedPurchaseOrder.originalDoc?.po_id
          });
          
          const result = await purchaseOrderApi.getPurchaseOrder(poIdToFetch);
          console.log('[Fdoc] Purchase Order API response:', {
            success: result.success,
            hasPurchaseOrder: !!result.purchaseOrder,
            hasLines: !!result.lines,
            linesCount: result.lines?.length,
            purchaseOrder: result.purchaseOrder
          });
          
          if (result.success && result.purchaseOrder) {
            const mapped = mapPurchaseOrderToUI({
              ...result.purchaseOrder,
              lines: result.lines || [],
            });
            console.log('[Fdoc] Mapped Purchase Order:', {
              poNumber: mapped.poNumber,
              vendor: mapped.vendor,
              paymentMode: mapped.paymentMode,
              itemsCount: mapped.items?.length,
              items: mapped.items,
              subtotal: mapped.subtotal,
              discount: mapped.discount,
              grandTotal: mapped.grandTotal
            });
            setPurchaseOrderDetails(prev => ({
              ...prev,
              [poId]: mapped,
            }));
            setSelectedPurchaseOrder(mapped);
          } else {
            console.error('[Fdoc] Failed to fetch Purchase Order:', result);
          }
        } else if (!isExternalDoc && poId) {
          // For Fdoc documents, check if we have originalDoc first
          let fullFdocDoc = selectedPurchaseOrder.originalDoc;
          
          // If no originalDoc, fetch from Fdoc API
          if (!fullFdocDoc) {
            console.log('[Fdoc] No originalDoc, fetching from Fdoc API');
            fullFdocDoc = await fdocApi.getDocument(poId);
          } else {
            console.log('[Fdoc] Using originalDoc from selectedPurchaseOrder');
          }
          
          console.log('[Fdoc] Full Fdoc document for Purchase Order:', {
            hasExtractedData: !!fullFdocDoc.extracted_data,
            hasFields: !!fullFdocDoc.extracted_data?.fields,
            extractedDataType: typeof fullFdocDoc.extracted_data,
            docNumber: fullFdocDoc.docNumber,
            date: fullFdocDoc.date,
            uploaded_at: fullFdocDoc.uploaded_at,
            file_name: fullFdocDoc.file_name
          });
          
          if (fullFdocDoc.extracted_data && fullFdocDoc.extracted_data.fields) {
            // Transform extracted_data to Purchase Order format
            const fields = fullFdocDoc.extracted_data.fields;
            
            console.log('[Fdoc] Extracted data structure:', {
              extractedDataType: typeof fullFdocDoc.extracted_data,
              extractedDataKeys: Object.keys(fullFdocDoc.extracted_data),
              hasFields: !!fullFdocDoc.extracted_data.fields,
              fieldsType: typeof fields,
              fieldsKeys: Object.keys(fields),
              fieldsValues: Object.values(fields),
              fields: fields,
              fullExtractedData: fullFdocDoc.extracted_data
            });
            
            // Log each key-value pair in fields to see what's actually there
            console.log('[Fdoc] Fields object details:');
            Object.keys(fields).forEach(key => {
              console.log(`  ${key}:`, fields[key], typeof fields[key]);
            });
            
            // Also check if data is at root level of extracted_data
            console.log('[Fdoc] Extracted data root level keys:', Object.keys(fullFdocDoc.extracted_data));
            Object.keys(fullFdocDoc.extracted_data).forEach(key => {
              if (key !== 'fields' && key !== 'type') {
                console.log(`  ${key}:`, fullFdocDoc.extracted_data[key], typeof fullFdocDoc.extracted_data[key]);
              }
            });
            
            // Check if fields contains raw_text and text_blocks (raw extraction)
            let extractedFields = {};
            if (typeof fields === 'object' && fields !== null) {
              const fieldKeys = Object.keys(fields);
              
              // If we have raw_text and text_blocks, parse them
              if (fields.text_blocks && Array.isArray(fields.text_blocks)) {
                console.log('[Fdoc] Parsing text_blocks array to extract structured data');
                const textBlocks = fields.text_blocks;
                
                // Parse text_blocks to extract structured data
                for (let i = 0; i < textBlocks.length; i++) {
                  const block = String(textBlocks[i]).trim();
                  const nextBlock = i < textBlocks.length - 1 ? String(textBlocks[i + 1]).trim() : '';
                  
                  // Extract PO Number
                  if (block.match(/^PO-?\d+$/i) || block.match(/PO\s*Number/i)) {
                    if (block.match(/^PO-?\d+$/i)) {
                      extractedFields.po_number = block;
                    } else if (nextBlock && nextBlock.match(/^PO-?\d+$/i)) {
                      extractedFields.po_number = nextBlock;
                    }
                  }
                  
                  // Extract PO Date
                  if (block.match(/PO\s*Date:/i)) {
                    extractedFields.po_date = nextBlock || '';
                  }
                  
                  // Extract Expected Delivery
                  if (block.match(/Expected\s*Delivery:/i)) {
                    extractedFields.expected_delivery_date = nextBlock || '';
                  }
                  
                  // Extract Vendor (after VENDOR label)
                  if (block.match(/^VENDOR$/i)) {
                    const vendorIdBlock = nextBlock || '';
                    // Check if nextBlock is a UUID (vendor ID)
                    if (vendorIdBlock.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                      extractedFields.vendor_id = vendorIdBlock;
                      console.log('[Fdoc] Found vendor ID:', vendorIdBlock);
                      // Skip the vendor ID and look for vendor name in subsequent blocks
                      // Vendor name should be before "BILLING SITE" or other labels
                      for (let j = i + 2; j < textBlocks.length && j < i + 5; j++) {
                        const potentialName = String(textBlocks[j]).trim();
                        // Stop if we hit another label
                        if (potentialName.match(/^(BILLING|SHIPPING|PAYMENT|SITE|MODE|DATE|ADDRESS)$/i)) {
                          break;
                        }
                        // If it's not a UUID and not empty, it might be the vendor name
                        if (potentialName && !potentialName.match(/^[0-9a-f-]{36}$/i) && potentialName.length > 2) {
                          extractedFields.vendor_name = potentialName;
                          console.log('[Fdoc] Found vendor name in text_blocks:', potentialName);
                          break;
                        }
                      }
                    } else {
                      // If nextBlock is not a UUID, it might be the vendor name directly
                      extractedFields.vendor_name = vendorIdBlock;
                    }
                  }
                  
                  // Extract Payment Mode - should be after "PAYMENT" but before "SHIPPING SITE" or "MODE"
                  // Only extract if we haven't already found it and if nextBlock is not a date or label
                  if (block.match(/^PAYMENT$/i) && nextBlock && !extractedFields.payment_mode) {
                    // Check if nextBlock is a date (contains day/month/year patterns)
                    const isDate = nextBlock.match(/\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i) ||
                                  nextBlock.match(/\d{4}-\d{2}-\d{2}/);
                    
                    // Check if nextBlock is a label (should be skipped)
                    const isLabel = nextBlock.match(/^(DATE|MODE|PAYMENT|SHIPPING|BILLING|SITE|ADDRESS)$/i);
                    
                    // Check if nextBlock looks like a payment mode (common payment terms)
                    const isPaymentMode = nextBlock.match(/(Net Banking|Cash|Cheque|Credit Card|Debit Card|UPI|Bank Transfer|Online|Offline)/i);
                    
                    if (!isDate && !isLabel && (isPaymentMode || nextBlock.length > 3)) {
                      extractedFields.payment_mode = nextBlock;
                      console.log('[Fdoc] Extracted payment mode:', nextBlock);
                    } else {
                      console.log('[Fdoc] Skipping payment mode extraction - nextBlock is date/label:', nextBlock);
                    }
                  }
                  
                  // Extract Payment Date - look for "PAYMENT DATE" or "PAYMENT" followed by "DATE"
                  if (block.match(/PAYMENT\s*DATE/i)) {
                    extractedFields.payment_date = nextBlock || '';
                    console.log('[Fdoc] Extracted payment date:', nextBlock);
                  } else if (block.match(/^PAYMENT$/i) && nextBlock && nextBlock.match(/^DATE$/i) && i + 2 < textBlocks.length) {
                    // Handle case where "PAYMENT" and "DATE" are separate blocks
                    const dateBlock = String(textBlocks[i + 2]).trim();
                    if (dateBlock.match(/\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i) || 
                        dateBlock.match(/\d{4}-\d{2}-\d{2}/)) {
                      extractedFields.payment_date = dateBlock;
                      console.log('[Fdoc] Extracted payment date from separate blocks:', dateBlock);
                    }
                  }
                  
                  // Extract Billing Address components
                  if (block.match(/BILLING\s*ADDRESS/i)) {
                    // Name is usually the next block
                    if (i + 1 < textBlocks.length) {
                      const nameBlock = String(textBlocks[i + 1]).trim();
                      if (nameBlock && !nameBlock.match(/@|\.com|Mobile|PAN|GST/i)) {
                        const nameParts = nameBlock.split(/\s+/);
                        if (nameParts.length >= 2) {
                          extractedFields.first_name = nameParts[0];
                          extractedFields.last_name = nameParts.slice(1).join(' ');
                        } else {
                          extractedFields.first_name = nameBlock;
                        }
                      }
                    }
                    
                    // Email is usually after name
                    if (i + 2 < textBlocks.length) {
                      const emailBlock = String(textBlocks[i + 2]).trim();
                      if (emailBlock.match(/@/)) {
                        extractedFields.email = emailBlock;
                      }
                    }
                    
                    // Address, city, state, country, pincode follow
                    let addressIndex = i + 1;
                    while (addressIndex < textBlocks.length && addressIndex < i + 10) {
                      const addrBlock = String(textBlocks[addressIndex]).trim();
                      
                      if (addrBlock.match(/Mobile:/i)) {
                        const mobileMatch = addrBlock.match(/Mobile:\s*(.+)/i);
                        if (mobileMatch) {
                          extractedFields.mobile_number = mobileMatch[1].trim();
                        }
                        break;
                      }
                      
                      if (addrBlock.match(/PAN:/i)) {
                        const panMatch = addrBlock.match(/PAN:\s*(.+)/i);
                        if (panMatch) {
                          extractedFields.pan_number = panMatch[1].trim();
                        }
                        continue;
                      }
                      
                      if (addrBlock.match(/GST:/i)) {
                        const gstMatch = addrBlock.match(/GST:\s*(.+)/i);
                        if (gstMatch) {
                          extractedFields.gst_number = gstMatch[1].trim();
                        }
                        continue;
                      }
                      
                      // Address parsing
                      if (!extractedFields.address && addrBlock && !addrBlock.match(/@|Mobile|PAN|GST|BILLING|ADDRESS/i)) {
                        if (!extractedFields.address) {
                          extractedFields.address = addrBlock;
                        } else if (!extractedFields.city && addrBlock.includes(',')) {
                          const cityState = addrBlock.split(',');
                          extractedFields.city = cityState[0].trim();
                          if (cityState[1]) {
                            extractedFields.state = cityState[1].trim();
                          }
                        } else if (!extractedFields.country) {
                          const countryPincode = addrBlock.split(/\s+/);
                          if (countryPincode.length >= 2) {
                            extractedFields.country = countryPincode[0];
                            extractedFields.pin_code = countryPincode[countryPincode.length - 1];
                          }
                        }
                      }
                      
                      addressIndex++;
                    }
                  }
                  
                  // Extract line items
                  if (block.match(/S\.NO|NAME|HSN|UNITS|RATE|TAX|AMOUNT/i) && !extractedFields.items) {
                    extractedFields.items = [];
                    // Start parsing items after header
                    let itemIndex = i + 1;
                    while (itemIndex < textBlocks.length) {
                      const itemBlock = String(textBlocks[itemIndex]).trim();
                      
                      // Stop at totals
                      if (itemBlock.match(/Subtotal|Discount|GRAND\s*TOTAL|Page/i)) {
                        break;
                      }
                      
                      // Try to parse item (format: number, name, hsn, units, rate, tax%, amount)
                      if (itemBlock.match(/^\d+$/) && itemIndex + 5 < textBlocks.length) {
                        const item = {
                          name: String(textBlocks[itemIndex + 1]).trim(),
                          hsn_code: String(textBlocks[itemIndex + 2]).trim(),
                          quantity: parseFloat(String(textBlocks[itemIndex + 3]).replace(/[^\d.]/g, '') || 0),
                          unit_cost: parseFloat(String(textBlocks[itemIndex + 4]).replace(/[^\d.]/g, '') || 0),
                          tax_rate: parseFloat(String(textBlocks[itemIndex + 5]).replace(/[^\d.]/g, '') || 0),
                          line_total: parseFloat(String(textBlocks[itemIndex + 6]).replace(/[^\d.]/g, '') || 0),
                        };
                        if (item.name && item.name !== 'HSN CODE' && item.name !== 'UNITS') {
                          extractedFields.items.push(item);
                          itemIndex += 6;
                          continue;
                        }
                      }
                      
                      itemIndex++;
                    }
                  }
                  
                  // Extract totals
                  if (block.match(/Subtotal/i)) {
                    const subtotalMatch = nextBlock.match(/₹?([\d,]+\.?\d*)/);
                    if (subtotalMatch) {
                      extractedFields.subtotal = parseFloat(subtotalMatch[1].replace(/,/g, ''));
                    }
                  }
                  
                  if (block.match(/Discount/i)) {
                    const discountMatch = nextBlock.match(/₹?([\d,]+\.?\d*)/);
                    if (discountMatch) {
                      extractedFields.discount = parseFloat(discountMatch[1].replace(/,/g, ''));
                    }
                  }
                  
                  if (block.match(/GRAND\s*TOTAL/i)) {
                    const totalMatch = nextBlock.match(/₹?([\d,]+\.?\d*)/);
                    if (totalMatch) {
                      extractedFields.grand_total = parseFloat(totalMatch[1].replace(/,/g, ''));
                    }
                  }
                }
                
                console.log('[Fdoc] Parsed structured data from text_blocks:', extractedFields);
              } else {
                // Use fields as-is if it's not raw extraction
                extractedFields = fields;
              }
            }
            
            // Use extractedFields
            const fieldsToUse = extractedFields;
            
            const items = (fieldsToUse.items || fieldsToUse.line_items || fieldsToUse.lines || []).map((item, index) => ({
              name: item.description || item.item_name || item.name || `Item ${index + 1}`,
              hsnCode: item.hsn_code || item.hsn || '',
              units: parseFloat(item.quantity || item.quantity_ordered || item.units || 0),
              rate: parseFloat(item.unit_cost || item.rate || item.price || 0),
              tax: parseFloat(item.tax_rate || item.tax || 0),
              amount: parseFloat(item.line_total || item.amount || item.total || 0),
            }));

            const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
            const freightTotal = parseFloat(fieldsToUse.freight_total || fieldsToUse.freight || 0);
            const grandTotal = parseFloat(fieldsToUse.grand_total || fieldsToUse.total || (subtotal + freightTotal));

            // Extract billing address - handle various structures
            let billingAddress = fieldsToUse.billing_address || fieldsToUse.billing || fieldsToUse.address || {};
            if (typeof billingAddress === 'string') {
              billingAddress = { address: billingAddress };
            }
            
            // Also check if address fields are at the root level of fieldsToUse
            const rootAddress = {
              firstName: fieldsToUse.first_name || fieldsToUse.firstName || billingAddress.first_name || billingAddress.firstName || '',
              lastName: fieldsToUse.last_name || fieldsToUse.lastName || billingAddress.last_name || billingAddress.lastName || '',
              email: fieldsToUse.email || billingAddress.email || '',
              address: fieldsToUse.address || fieldsToUse.street || fieldsToUse.line1 || billingAddress.address || billingAddress.street || billingAddress.line1 || '',
              city: fieldsToUse.city || billingAddress.city || '',
              state: fieldsToUse.state || billingAddress.state || '',
              country: fieldsToUse.country || billingAddress.country || 'India',
              pinCode: fieldsToUse.pin_code || fieldsToUse.pinCode || fieldsToUse.zip || fieldsToUse.pincode || billingAddress.pin_code || billingAddress.pinCode || billingAddress.zip || '',
              mobileNumber: fieldsToUse.mobile_number || fieldsToUse.mobileNumber || fieldsToUse.phone || fieldsToUse.contact || billingAddress.mobile_number || billingAddress.mobileNumber || billingAddress.phone || '',
              panNumber: fieldsToUse.pan_number || fieldsToUse.panNumber || fieldsToUse.pan || billingAddress.pan_number || billingAddress.panNumber || '',
              gstNumber: fieldsToUse.gst_number || fieldsToUse.gstNumber || fieldsToUse.gst || fieldsToUse.gstin || billingAddress.gst_number || billingAddress.gstNumber || billingAddress.gst || '',
            };
            
            // Get date from various sources
            const docDate = fullFdocDoc.uploaded_at 
              ? new Date(fullFdocDoc.uploaded_at).toISOString().split('T')[0]
              : (fullFdocDoc.date || normalizeDate(fieldsToUse.po_date || fieldsToUse.date || fieldsToUse.issue_date || ''));
            
            // Get docNumber from various sources
            const docNumber = fieldsToUse.po_number || fieldsToUse.poNumber || fieldsToUse.number || fieldsToUse.po_no || 
                             selectedPurchaseOrder.docNumber || fullFdocDoc.docNumber || 
                             `PO-${poId.substring(0, 8).toUpperCase()}`;
            
            // Get vendor from various sources - check all possible locations
            let vendor = fieldsToUse.vendor_name || fieldsToUse.vendorName || fieldsToUse.vendor || 
                        fieldsToUse.supplier_name || fieldsToUse.supplierName || fieldsToUse.supplier ||
                        fieldsToUse.company_name || fieldsToUse.companyName || 
                        fieldsToUse.buyer_name || fieldsToUse.buyerName || fieldsToUse.buyer ||
                        fieldsToUse.customer_name || fieldsToUse.customerName || fieldsToUse.customer ||
                        '';
            
            // If we have vendor_id but no vendor name (or vendor name is invalid), try to look it up
            if ((!vendor || vendor === 'BILLING SITE' || vendor.match(/^(BILLING|SHIPPING|PAYMENT|SITE|MODE|DATE|ADDRESS)$/i)) && fieldsToUse.vendor_id) {
              console.log('[Fdoc] Found vendor_id, attempting to look up vendor name:', fieldsToUse.vendor_id);
              try {
                // Import vendorApi if available
                const { vendorApi } = await import('../Vender/vendorApi');
                const vendorResult = await vendorApi.getVendor(fieldsToUse.vendor_id);
                console.log('[Fdoc] Vendor lookup result:', vendorResult);
                if (vendorResult.success && vendorResult.vendor) {
                  vendor = vendorResult.vendor.vendor_name || vendorResult.vendor.vendorName || vendorResult.vendor.name || '';
                  console.log('[Fdoc] Successfully looked up vendor name from ID:', vendor);
                } else {
                  console.warn('[Fdoc] Vendor lookup failed or returned no vendor:', vendorResult);
                }
              } catch (err) {
                console.error('[Fdoc] Error looking up vendor name:', err);
              }
            }
            
            // If vendor not found in fieldsToUse, check in nested objects
            if (!vendor) {
              // Check if vendor is in a nested object
              Object.keys(fieldsToUse).forEach(key => {
                const value = fieldsToUse[key];
                if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                  const nestedVendor = value.vendor_name || value.vendorName || value.vendor || 
                                     value.supplier_name || value.supplierName || value.supplier ||
                                     value.company_name || value.companyName || '';
                  if (nestedVendor && !vendor) {
                    vendor = nestedVendor;
                    console.log(`[Fdoc] Found vendor in nested object "${key}":`, vendor);
                  }
                }
              });
            }
            
            // Check in extracted_data root level
            if (!vendor && fullFdocDoc.extracted_data) {
              const rootVendor = fullFdocDoc.extracted_data.vendor_name || 
                                fullFdocDoc.extracted_data.vendorName || 
                                fullFdocDoc.extracted_data.vendor ||
                                fullFdocDoc.extracted_data.supplier_name ||
                                fullFdocDoc.extracted_data.supplier || '';
              if (rootVendor) {
                vendor = rootVendor;
                console.log('[Fdoc] Found vendor in extracted_data root level:', vendor);
              }
            }
            
            // Check in originalDoc if available
            if (!vendor && selectedPurchaseOrder.originalDoc) {
              const originalVendor = selectedPurchaseOrder.originalDoc.extracted_data?.fields?.vendor_name ||
                                    selectedPurchaseOrder.originalDoc.extracted_data?.fields?.vendor ||
                                    selectedPurchaseOrder.originalDoc.extracted_data?.vendor_name ||
                                    selectedPurchaseOrder.originalDoc.vendor_name ||
                                    selectedPurchaseOrder.originalDoc.vendor || '';
              if (originalVendor) {
                vendor = originalVendor;
                console.log('[Fdoc] Found vendor in originalDoc:', vendor);
              }
            }
            
            // Final fallback - use billing address name or vendor ID
            if (!vendor) {
              if (rootAddress.firstName && rootAddress.lastName) {
                vendor = `${rootAddress.firstName} ${rootAddress.lastName}`.trim();
              } else if (fieldsToUse.vendor_id) {
                vendor = `Vendor ID: ${fieldsToUse.vendor_id.substring(0, 8)}`;
              } else {
                vendor = 'Unknown Vendor';
              }
            }
            
            console.log('[Fdoc] Final vendor value:', vendor);
            
            // Get payment mode from various sources
            let paymentMode = fieldsToUse.payment_terms || fieldsToUse.paymentTerms ||
                              fieldsToUse.payment_mode || fieldsToUse.paymentMode || 
                              fieldsToUse.payment_method || fieldsToUse.paymentMethod || 
                              fieldsToUse.payment ||
                              '';
            
            // Validate payment mode - it shouldn't be a date or label
            if (paymentMode) {
              const isDate = paymentMode.match(/\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i) ||
                            paymentMode.match(/\d{4}-\d{2}-\d{2}/) ||
                            paymentMode.match(/^(DATE|MODE|PAYMENT|SHIPPING|BILLING|SITE|ADDRESS)$/i);
              
              if (isDate) {
                console.warn('[Fdoc] Payment mode appears to be a date or invalid, clearing it:', paymentMode);
                paymentMode = '';
              }
            }
            
            // If payment mode is empty or invalid, set default
            if (!paymentMode) {
              paymentMode = 'Not specified';
            }
            
            console.log('[Fdoc] Final payment mode:', paymentMode);
            
            // Get site info - check if site is stored in the document or use selected site
            const siteId = selectedPurchaseOrder.siteId || fullFdocDoc.siteId || fullFdocDoc.site_id || 
                          fieldsToUse.site_id || fieldsToUse.siteId || 
                          (selectedSite ? resolveSiteValue(selectedSite) : '');
            const siteName = selectedPurchaseOrder.siteName || fullFdocDoc.siteName || 
                            fieldsToUse.site_name || fieldsToUse.siteName || 
                            (selectedSite ? (selectedSite.site_name || selectedSite.name || '') : '') || '';
            
            console.log('[Fdoc] Mapped Purchase Order data:', {
              poNumber: docNumber,
              vendor: vendor,
              paymentMode: paymentMode,
              siteName: siteName,
              panNumber: rootAddress.panNumber,
              gstNumber: rootAddress.gstNumber,
              billingAddress: rootAddress
            });
            
            const mapped = {
              id: fullFdocDoc.fdoc_id || poId,
              poNumber: docNumber,
              vendor: vendor,
              poDate: normalizeDate(fieldsToUse.po_date || fieldsToUse.date || fieldsToUse.issue_date || fieldsToUse.document_date || docDate),
              expectedDeliveryDate: normalizeDate(fieldsToUse.expected_delivery_date || fieldsToUse.delivery_date || fieldsToUse.expected_date || ''),
              paymentDate: normalizeDate(fieldsToUse.payment_date || fieldsToUse.paid_date || ''),
              paymentMode: paymentMode,
              siteId: siteId,
              siteName: siteName,
              billingAddress: rootAddress,
              items,
              subtotal,
              discount: parseFloat(fieldsToUse.discount || 0),
              grandTotal,
              status: fieldsToUse.status || 'Pending',
            };
            setPurchaseOrderDetails(prev => ({
              ...prev,
              [poId]: mapped,
            }));
            setSelectedPurchaseOrder(mapped);
          }
        }
      } catch (err) {
        console.error('[Fdoc] Error fetching Purchase Order details:', err);
        const isFileMissing = err.status === 410 || err.fileMissing === true;
        if (isFileMissing && selectedPurchaseOrder) {
          const fdocId = selectedPurchaseOrder.fdoc_id || selectedPurchaseOrder.id;
          await autoCancelMissingFdoc(fdocId, 'file missing (410), auto-deleted when opening Purchase Order');
          setDocuments((prev) => prev.filter((d) => (d.fdoc_id || d.id) !== fdocId));
          setSelectedPurchaseOrder(null);
          setPurchaseOrderDetails((prev) => {
            const next = { ...prev };
            delete next[fdocId];
            return next;
          });
        }
      } finally {
        setIsLoadingPurchaseOrderDetails(false);
        fetchingPurchaseOrderRef.current = false;
      }
    };

    fetchPurchaseOrderDetails();
  }, [selectedPurchaseOrder?.id, selectedPurchaseOrder?.fdoc_id]);

  // Fetch Invoice details when a card is selected
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      console.log('[Fdoc] Invoice details useEffect triggered:', {
        hasSelectedInvoice: !!selectedInvoice,
        selectedInvoice: selectedInvoice,
        invoiceId: selectedInvoice?.id || selectedInvoice?.fdoc_id
      });
      
      if (!selectedInvoice) {
        console.log('[Fdoc] No selectedInvoice, returning');
        return;
      }

      const invoiceId = selectedInvoice.id || selectedInvoice.fdoc_id;
      if (!invoiceId) {
        console.log('[Fdoc] No invoiceId found, returning');
        return;
      }

      if (selectedInvoice.fileMissing || selectedInvoice.apiStatus === 'file_missing') {
        setIsLoadingInvoiceDetails(false);
        return;
      }
      
      console.log('[Fdoc] Starting Invoice details fetch for:', invoiceId);

      // Check if we already have the details cached
      if (invoiceDetails[invoiceId] && invoiceDetails[invoiceId].invoiceNumber && invoiceDetails[invoiceId].invoiceNumber !== 'N/A') {
        // Already have details, just update the selected invoice
        setSelectedInvoice(invoiceDetails[invoiceId]);
        return;
      }

      // Check if we're already fetching this invoice to prevent duplicate calls
      if (fetchingInvoiceRef.current) {
        console.log('[Fdoc] Already fetching Invoice details, skipping duplicate call');
        return;
      }

      fetchingInvoiceRef.current = true;
      setIsLoadingInvoiceDetails(true);
      try {
        const isExternalDoc = selectedInvoice.externalDoc === true;

        // For external documents, fetch from Invoice API
        if (isExternalDoc && invoiceId) {
          const invoiceIdToFetch = selectedInvoice.originalDoc?.invoice_id || 
                                 selectedInvoice.originalDoc?.id || 
                                 invoiceId;
          
          console.log('[Fdoc] Fetching external Invoice details:', {
            selectedId: invoiceId,
            invoiceIdToFetch: invoiceIdToFetch,
            hasOriginalDoc: !!selectedInvoice.originalDoc,
            originalDocInvoiceId: selectedInvoice.originalDoc?.invoice_id
          });
          
          const result = await invoiceApi.getInvoice(invoiceIdToFetch);
          console.log('[Fdoc] Invoice API response:', {
            success: result.success,
            hasInvoice: !!result.invoice,
            hasLines: !!result.lines,
            linesCount: result.lines?.length,
            invoice: result.invoice
          });
          
          if (result.success && result.invoice) {
            const mapped = mapInvoiceToUI(result.invoice, result.lines || []);
            console.log('[Fdoc] Mapped Invoice:', {
              invoiceNumber: mapped.invoiceNumber,
              client: mapped.client,
              paymentTerms: mapped.paymentTerms,
              siteName: mapped.siteName,
              itemsCount: mapped.lineItems?.length,
              items: mapped.lineItems,
              subtotal: mapped.subtotal,
              grandTotal: mapped.grandTotal
            });
            setInvoiceDetails(prev => ({
              ...prev,
              [invoiceId]: mapped,
            }));
            setSelectedInvoice(mapped);
          } else {
            console.error('[Fdoc] Failed to fetch Invoice:', result);
          }
        } else if (!isExternalDoc && invoiceId) {
          // For Fdoc documents, check if we have originalDoc first
          let fullFdocDoc = selectedInvoice.originalDoc;
          
          console.log('[Fdoc] Processing FDOC Invoice document:', {
            invoiceId: invoiceId,
            hasOriginalDoc: !!fullFdocDoc,
            originalDocHasExtractedData: !!fullFdocDoc?.extracted_data,
            originalDocHasFields: !!fullFdocDoc?.extracted_data?.fields,
            originalDocKeys: fullFdocDoc ? Object.keys(fullFdocDoc) : []
          });
          
          // If no originalDoc or originalDoc doesn't have extracted_data, fetch from Fdoc API
          if (!fullFdocDoc || !fullFdocDoc.extracted_data) {
            console.log('[Fdoc] No originalDoc or missing extracted_data, fetching from Fdoc API');
            try {
              fullFdocDoc = await fdocApi.getDocument(invoiceId);
              console.log('[Fdoc] Fetched document from API:', {
                hasExtractedData: !!fullFdocDoc?.extracted_data,
                hasFields: !!fullFdocDoc?.extracted_data?.fields
              });
            } catch (fetchError) {
              console.error('[Fdoc] Failed to fetch document from API:', fetchError);
              // If fetch fails but we have originalDoc, try to use it anyway
              if (fullFdocDoc) {
                console.log('[Fdoc] Using originalDoc despite missing extracted_data');
              } else {
                throw fetchError;
              }
            }
          } else {
            console.log('[Fdoc] Using originalDoc from selectedInvoice');
          }
          
          console.log('[Fdoc] Full Fdoc document for Invoice:', {
            hasExtractedData: !!fullFdocDoc.extracted_data,
            hasFields: !!fullFdocDoc.extracted_data?.fields,
            extractedDataType: typeof fullFdocDoc.extracted_data,
            docNumber: fullFdocDoc.docNumber,
            date: fullFdocDoc.date,
            uploaded_at: fullFdocDoc.uploaded_at,
            file_name: fullFdocDoc.file_name
          });
          
          if (fullFdocDoc.extracted_data) {
            // Check if data is in fields or at root level
            // For Invoice, data might be in extracted_data.fields or at extracted_data root
            const extractedDataRoot = fullFdocDoc.extracted_data;
            let fields = extractedDataRoot.fields;
            
            // If fields is missing or empty, check if data is at root level
            if (!fields || (typeof fields === 'object' && Object.keys(fields).length === 0)) {
              console.log('[Fdoc] No fields object, checking root level of extracted_data');
              // Check if invoice data is at root level
              if (extractedDataRoot.invoice_number || extractedDataRoot.customer_name || extractedDataRoot.line_items) {
                fields = extractedDataRoot;
                console.log('[Fdoc] Using root level data as fields');
              }
            }
            
            // If still no fields, create empty object
            if (!fields) {
              fields = {};
            }
            
            console.log('[Fdoc] Invoice extracted_data structure:', {
              hasExtractedData: !!fullFdocDoc.extracted_data,
              extractedDataKeys: Object.keys(extractedDataRoot),
              extractedDataRoot: extractedDataRoot,
              hasFields: !!fullFdocDoc.extracted_data.fields,
              fieldsType: typeof fields,
              fieldsKeys: fields ? Object.keys(fields) : [],
              hasTextBlocks: !!fields?.text_blocks,
              textBlocksType: typeof fields?.text_blocks,
              textBlocksLength: Array.isArray(fields?.text_blocks) ? fields.text_blocks.length : 0,
              textBlocksPreview: Array.isArray(fields?.text_blocks) ? fields.text_blocks.slice(0, 10) : null,
              hasLineItems: !!(fields?.line_items && Array.isArray(fields.line_items) && fields.line_items.length > 0),
              hasItems: !!(fields?.items && Array.isArray(fields.items) && fields.items.length > 0),
              hasCustomerName: !!(fields?.customer_name || fields?.customerName),
              customerName: fields?.customer_name || fields?.customerName,
              hasPaymentTerms: !!(fields?.payment_terms || fields?.paymentTerms),
              paymentTerms: fields?.payment_terms || fields?.paymentTerms,
              hasPaymentMode: !!(fields?.payment_mode || fields?.paymentMode),
              paymentMode: fields?.payment_mode || fields?.paymentMode,
              hasSubtotal: !!(fields?.subtotal || fields?.sub_total),
              subtotal: fields?.subtotal || fields?.sub_total,
              hasTaxTotal: !!(fields?.tax_total || fields?.tax_amount),
              taxTotal: fields?.tax_total || fields?.tax_amount,
              hasGrandTotal: !!(fields?.grand_total || fields?.total_amount),
              grandTotal: fields?.grand_total || fields?.total_amount,
              lineItemsPreview: fields?.line_items ? (Array.isArray(fields.line_items) ? fields.line_items.slice(0, 3) : fields.line_items) : null,
              itemsPreview: fields?.items ? (Array.isArray(fields.items) ? fields.items.slice(0, 3) : fields.items) : null,
              fullFields: JSON.parse(JSON.stringify(fields)) // Deep clone to see full structure
            });
            
            // Check if we have structured fields first
            const hasStructuredData = fields && (
              fields.customer_name || fields.customerName || 
              fields.invoice_number || fields.invoiceNumber ||
              (fields.line_items && Array.isArray(fields.line_items) && fields.line_items.length > 0) ||
              (fields.items && Array.isArray(fields.items) && fields.items.length > 0)
            );
            
            console.log('[Fdoc] Has structured data in fields:', hasStructuredData, {
              hasFields: !!fields,
              hasCustomerName: !!(fields?.customer_name || fields?.customerName),
              hasInvoiceNumber: !!(fields?.invoice_number || fields?.invoiceNumber),
              hasLineItems: !!((fields?.line_items && Array.isArray(fields.line_items) && fields.line_items.length > 0) || 
                              (fields?.items && Array.isArray(fields.items) && fields.items.length > 0)),
              hasPaymentTerms: !!(fields?.payment_terms || fields?.paymentTerms),
              hasSubtotal: !!(fields?.subtotal || fields?.sub_total),
              hasGrandTotal: !!(fields?.grand_total || fields?.total_amount)
            });
            
            // Parse text_blocks if structured fields are missing or incomplete (similar to Purchase Order)
            let parsedFields = {};
            if (fields && (!hasStructuredData || !fields.customer_name)) {
              if (fields.text_blocks && Array.isArray(fields.text_blocks)) {
              console.log('[Fdoc] Parsing text_blocks array to extract Invoice structured data, text_blocks length:', fields.text_blocks.length);
              const textBlocks = fields.text_blocks.map(block => String(block).trim()).filter(Boolean);
              console.log('[Fdoc] First 20 text_blocks:', textBlocks.slice(0, 20));
              
              let currentSection = '';
              let addressBlocks = [];
              let itemsStarted = false;
              let currentItem = {};
              let items = [];
              let customerName = '';
              let paymentTermsFound = false;
              
              for (let i = 0; i < textBlocks.length; i++) {
                const block = textBlocks[i];
                const nextBlock = textBlocks[i + 1];
                const nextNextBlock = textBlocks[i + 2];
                
                // Extract Invoice Number - check for "Invoice Number" label followed by number
                if (block.match(/^Invoice\s*Number$/i) || block.match(/Invoice\s*Number:/i) || block.match(/INVOICE\s*NO/i)) {
                  // Check next block for invoice number
                  if (nextBlock && !nextBlock.match(/^(Invoice|Date|Order|BILL|SHIP|PAYMENT|SITE|MODE|ADDRESS|ITEM|S\.NO|NAME|QUANTITY|RATE|AMOUNT|Subtotal|Discount|GRAND TOTAL)$/i)) {
                    parsedFields.invoice_number = nextBlock.trim();
                    console.log('[Fdoc] Extracted invoice number:', parsedFields.invoice_number);
                  }
                }
                // Also check if block itself is an invoice number pattern
                else if (block.match(/^INV-?\d+$/i) || block.match(/^[A-Z0-9]{6,}$/i) && !block.match(/^(BILL|SHIP|PAYMENT|SITE|MODE|DATE|ADDRESS|INVOICE|ITEM|S\.NO|NAME|QUANTITY|RATE|AMOUNT|Subtotal|Discount|GRAND TOTAL|Original|Recipient|Supply|Number|Date)$/i)) {
                  // Check if previous block was "Invoice Number"
                  if (i > 0 && textBlocks[i - 1].match(/^Invoice\s*Number$/i)) {
                    parsedFields.invoice_number = block.trim();
                    console.log('[Fdoc] Extracted invoice number from block:', parsedFields.invoice_number);
                  }
                }
                
                // Extract Invoice Date - check for "Invoice Date" label followed by date
                if (block.match(/^Invoice\s*Date$/i) || block.match(/Invoice\s*Date:/i)) {
                  if (nextBlock && nextBlock.match(/\d{2}-\d{2}-\d{4}/) || nextBlock.match(/\d{4}-\d{2}-\d{2}/)) {
                    parsedFields.invoice_date = nextBlock.trim();
                    console.log('[Fdoc] Extracted invoice date:', parsedFields.invoice_date);
                  }
                }
                
                // Extract Customer Name - check for "BILL TO:" or "Tax Invoice" followed by name
                if (block.match(/^(BILL\s*TO|CUSTOMER|CLIENT|BILLING\s*TO)$/i)) {
                  // Look for customer name in next few blocks (skip "Tax Invoice", "Original For Recipient", etc.)
                  for (let j = i + 1; j < textBlocks.length && j < i + 6; j++) {
                    const potentialName = String(textBlocks[j]).trim();
                    // Skip common labels and patterns
                    if (potentialName.match(/^(Tax\s*Invoice|Original\s*For\s*Recipient|BILLING|SHIPPING|PAYMENT|SITE|MODE|DATE|ADDRESS|INVOICE|ITEM|S\.NO|NAME|QUANTITY|RATE|AMOUNT|Subtotal|Discount|GRAND TOTAL|Order\s*Number|Invoice\s*Number|Invoice\s*Date|Order\s*Date|Place\s*of\s*Supply)$/i)) {
                      continue;
                    }
                    // Skip UUIDs, dates, and pure numbers
                    if (potentialName && !potentialName.match(/^[0-9a-f-]{36}$/i) && 
                        potentialName.length > 2 && 
                        !potentialName.match(/^\d+$/) &&
                        !potentialName.match(/^\d{2}-\d{2}-\d{4}/) &&
                        !potentialName.match(/^\d{4}-\d{2}-\d{2}/) &&
                        !potentialName.match(/^[A-Z0-9]{10,}$/)) { // Skip long alphanumeric codes
                      customerName = potentialName;
                      parsedFields.customer_name = customerName;
                      console.log('[Fdoc] Found customer name in text_blocks:', customerName, 'at index', j);
                      break;
                    }
                  }
                }
                // Also check if "Tax Invoice" is followed by customer name
                else if (block.match(/^Tax\s*Invoice$/i) && nextBlock && 
                         !nextBlock.match(/^(Original|For|Recipient|BILL|SHIP|PAYMENT|SITE|MODE|DATE|ADDRESS|INVOICE|ITEM|S\.NO|NAME|QUANTITY|RATE|AMOUNT|Subtotal|Discount|GRAND TOTAL|Order|Number|Date)$/i) &&
                         !nextBlock.match(/^[0-9a-f-]{36}$/i) &&
                         nextBlock.length > 2 &&
                         !nextBlock.match(/^\d+$/) &&
                         !nextBlock.match(/^\d{2}-\d{2}-\d{4}/)) {
                  customerName = nextBlock.trim();
                  parsedFields.customer_name = customerName;
                  console.log('[Fdoc] Found customer name after Tax Invoice:', customerName);
                }
                
                // Extract Payment Terms
                if ((block.match(/^PAYMENT\s*TERMS$/i) || block.match(/Payment\s*Terms:/i)) && !paymentTermsFound) {
                  const isDate = nextBlock.match(/\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i) ||
                                 nextBlock.match(/\d{4}-\d{2}-\d{2}/) ||
                                 nextBlock.match(/DATE|MODE/i);
                  
                  if (!isDate && nextBlock) {
                    parsedFields.payment_terms = nextBlock;
                    paymentTermsFound = true;
                    console.log('[Fdoc] Extracted payment terms:', nextBlock);
                  }
                }
                
                // Extract Payment Mode
                if (block.match(/^PAYMENT\s*MODE$/i) || block.match(/Payment\s*Mode:/i)) {
                  const isDate = nextBlock.match(/\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i) ||
                                 nextBlock.match(/\d{4}-\d{2}-\d{2}/);
                  
                  if (!isDate && nextBlock) {
                    parsedFields.payment_mode = nextBlock;
                    console.log('[Fdoc] Extracted payment mode:', nextBlock);
                  }
                }
                
                // Extract PO Number
                if (block.match(/^PO\s*Number$/i) || block.match(/PO\s*Number:/i) || block.match(/^P\.O\.\s*Number$/i)) {
                  if (nextBlock) {
                    parsedFields.po_number = nextBlock.trim();
                    console.log('[Fdoc] Extracted PO number:', parsedFields.po_number);
                  }
                }
                
                // Extract Mode of transaction
                if (block.match(/^Mode\s*of\s*transaction$/i) || block.match(/Mode\s*of\s*transaction:/i)) {
                  if (nextBlock) {
                    parsedFields.mode_of_transaction = nextBlock.trim();
                    console.log('[Fdoc] Extracted mode of transaction:', parsedFields.mode_of_transaction);
                  }
                }
                
                // Extract Billing Address components
                if (block.match(/BILLING\s*ADDRESS/i) || block.match(/BILL\s*TO/i)) {
                  currentSection = 'BILLING_ADDRESS';
                  addressBlocks = [];
                  for (let j = i + 1; j < textBlocks.length; j++) {
                    const addrBlock = String(textBlocks[j]).trim();
                    if (addrBlock.match(/^(S\.NO|NAME|QUANTITY|RATE|AMOUNT|ITEM|Subtotal|Discount|GRAND TOTAL|SHIPPING|PAYMENT)$/i)) {
                      break;
                    }
                    if (addrBlock && !addrBlock.match(/^(BILLING|SHIPPING|PAYMENT|SITE|MODE|DATE|ADDRESS)$/i)) {
                      addressBlocks.push(addrBlock);
                    }
                  }
                  
                  if (addressBlocks.length > 0) {
                    parsedFields.billing_address = {
                      street: addressBlocks[0] || '',
                      city: addressBlocks[1] || '',
                      state: addressBlocks[2] || '',
                      pincode: addressBlocks[3] || '',
                    };
                  }
                }
                
                // Extract Line Items - look for table headers
                if (!itemsStarted && (
                  (block.match(/^S\.NO$/i) && textBlocks[i + 1]?.match(/^(NAME|ITEM|DESCRIPTION|PRODUCT)$/i)) ||
                  (block.match(/^(ITEM|PRODUCT|DESCRIPTION)$/i) && textBlocks[i + 1]?.match(/^(QUANTITY|QTY|QTY\.)$/i)) ||
                  (block.match(/^S\.NO$/i) && textBlocks[i + 2]?.match(/^(QUANTITY|QTY|QTY\.)$/i))
                )) {
                  itemsStarted = true;
                  console.log('[Fdoc] Line items table detected at index', i);
                  // Skip header row - find how many columns
                  let headerCount = 1;
                  for (let h = i + 1; h < textBlocks.length && h < i + 10; h++) {
                    if (textBlocks[h].match(/^(QUANTITY|QTY|RATE|PRICE|AMOUNT|TOTAL|Subtotal|Discount|GRAND TOTAL)$/i)) {
                      headerCount++;
                    } else if (textBlocks[h].match(/^\d+$/)) {
                      // Hit a number, this is likely the first item
                      break;
                    } else {
                      headerCount++;
                    }
                  }
                  i += headerCount - 1; // Move past headers
                  continue;
                }
                
                if (itemsStarted) {
                  // Check for totals/summary rows
                  if (block.match(/^(Subtotal|Sub\s*Total|Discount|GRAND\s*TOTAL|TOTAL|Total\s*Amount)$/i)) {
                    itemsStarted = false;
                    // Try to extract the value from next block or current block
                    let valueStr = nextBlock || block;
                    // Remove currency symbols and parse
                    const value = parseFloat(valueStr.toString().replace(/[₹,Rs\s]/g, '')) || 0;
                    if (block.match(/^Subtotal|Sub\s*Total$/i)) {
                      parsedFields.subtotal = value;
                      console.log('[Fdoc] Extracted subtotal:', parsedFields.subtotal);
                    } else if (block.match(/^Discount$/i)) {
                      parsedFields.discount = value;
                      console.log('[Fdoc] Extracted discount:', parsedFields.discount);
                    } else if (block.match(/^GRAND\s*TOTAL|TOTAL|Total\s*Amount$/i)) {
                      parsedFields.grand_total = value;
                      console.log('[Fdoc] Extracted grand total:', parsedFields.grand_total);
                    }
                    continue;
                  }
                  
                  // Try to extract line item - look for patterns like: number, name, quantity, rate, amount
                  // Pattern 1: Sequential - S.NO, NAME, QTY, RATE, AMOUNT
                  if (block.match(/^\d+$/) && i + 4 < textBlocks.length) {
                    const potentialItem = {
                      name: textBlocks[i + 1]?.trim() || '',
                      quantity: 0,
                      units: 'pcs',
                      rate: 0,
                      amount: 0,
                    };
                    
                    // Try to parse quantity (could be in i+2 or i+3)
                    const qtyStr = textBlocks[i + 2] || textBlocks[i + 3] || '0';
                    potentialItem.quantity = parseFloat(qtyStr.toString().replace(/[₹,]/g, '')) || 0;
                    
                    // Try to parse rate (could be in i+3 or i+4)
                    const rateStr = textBlocks[i + 3] || textBlocks[i + 4] || '0';
                    potentialItem.rate = parseFloat(rateStr.toString().replace(/[₹,Rs\s]/g, '')) || 0;
                    
                    // Try to parse amount (usually last in sequence)
                    const amountStr = textBlocks[i + 4] || textBlocks[i + 5] || '0';
                    potentialItem.amount = parseFloat(amountStr.toString().replace(/[₹,Rs\s]/g, '')) || 0;
                    
                    // Only add if we have at least a name and some numeric data
                    if (potentialItem.name && potentialItem.name.length > 0 && 
                        !potentialItem.name.match(/^(S\.NO|NAME|QUANTITY|RATE|AMOUNT|ITEM|Subtotal|Discount|GRAND TOTAL)$/i) &&
                        (potentialItem.quantity > 0 || potentialItem.rate > 0 || potentialItem.amount > 0)) {
                      items.push(potentialItem);
                      console.log('[Fdoc] Extracted line item:', potentialItem);
                      // Skip past this item's data (adjust based on actual structure)
                      i += 4; // Move past this item
                    }
                  }
                }
              }
              parsedFields.line_items = items;
              console.log('[Fdoc] Parsed Invoice structured data from text_blocks:', parsedFields);
              }
            }
            
            // Merge parsedFields with existing fields, giving precedence to parsedFields
            const finalFields = fields ? { ...fields, ...parsedFields } : parsedFields;
            
            console.log('[Fdoc] Final fields after merging:', {
              invoice_number: finalFields.invoice_number,
              customer_name: finalFields.customer_name,
              payment_terms: finalFields.payment_terms,
              payment_mode: finalFields.payment_mode,
              subtotal: finalFields.subtotal,
              tax_total: finalFields.tax_total,
              grand_total: finalFields.grand_total,
              hasLineItems: !!finalFields.line_items,
              lineItemsCount: Array.isArray(finalFields.line_items) ? finalFields.line_items.length : 0,
              lineItems: finalFields.line_items
            });
            
            // Extract line items - check multiple possible locations and formats
            let lineItems = [];
            
            // Check for line_items array
            if (finalFields.line_items && Array.isArray(finalFields.line_items) && finalFields.line_items.length > 0) {
              lineItems = finalFields.line_items;
              console.log('[Fdoc] Using line_items from finalFields:', lineItems.length, 'items');
            } 
            // Check for items array
            else if (finalFields.items && Array.isArray(finalFields.items) && finalFields.items.length > 0) {
              lineItems = finalFields.items;
              console.log('[Fdoc] Using items from finalFields:', lineItems.length, 'items');
            } 
            // Check for lineItems (camelCase)
            else if (finalFields.lineItems && Array.isArray(finalFields.lineItems) && finalFields.lineItems.length > 0) {
              lineItems = finalFields.lineItems;
              console.log('[Fdoc] Using lineItems from finalFields:', lineItems.length, 'items');
            }
            // Check if line_items is an object (might be structured differently)
            else if (finalFields.line_items && typeof finalFields.line_items === 'object' && !Array.isArray(finalFields.line_items)) {
              // Try to convert object to array
              const itemsObj = finalFields.line_items;
              lineItems = Object.keys(itemsObj).map(key => {
                const item = itemsObj[key];
                if (Array.isArray(item)) {
                  return item;
                }
                return item;
              }).flat().filter(Boolean);
              console.log('[Fdoc] Converted line_items object to array:', lineItems.length, 'items');
            }
            // Check parsedFields for line_items from text_blocks
            else if (parsedFields.line_items && Array.isArray(parsedFields.line_items) && parsedFields.line_items.length > 0) {
              lineItems = parsedFields.line_items;
              console.log('[Fdoc] Using line_items from parsedFields (text_blocks):', lineItems.length, 'items');
            }
            else {
              console.log('[Fdoc] No line items found in finalFields or parsedFields');
              console.log('[Fdoc] Available keys in finalFields:', Object.keys(finalFields));
            }
            
            // Get site information
            const siteId = fullFdocDoc.site_id || selectedInvoice.siteId || null;
            let siteName = 'N/A';
            if (siteId && sites.length > 0) {
              const site = sites.find(s => resolveSiteValue(s) === siteId);
              siteName = site?.site_name || site?.name || 'N/A';
              console.log('[Fdoc] Site lookup:', { siteId, siteName, sitesCount: sites.length });
            } else {
              console.log('[Fdoc] Site lookup failed:', { siteId, sitesCount: sites.length });
            }
            
            // Parse notes for PO Number, Payment Mode, Mode of transaction, Transaction ID, Amount paid (fallback when not in extracted fields)
            const notesText = String(finalFields.notes || '').trim();
            let notesPo = '';
            let notesPaymentMode = '';
            let notesModeOfTransaction = '';
            let notesTransactionId = '';
            let notesAmountPaid = '';
            if (notesText) {
              notesText.split(/\r?\n/).forEach((line) => {
                const m = line.match(/^([^:]+):\s*(.*)$/);
                if (!m) return;
                const key = m[1].trim().toLowerCase();
                const value = m[2].trim();
                if (key === 'po number') notesPo = value;
                else if (key === 'payment mode') notesPaymentMode = value;
                else if (key === 'mode of transaction') notesModeOfTransaction = value;
                else if (key === 'transaction id') notesTransactionId = value;
                else if (key === 'amount paid') notesAmountPaid = value.replace(/[₹,\s]/g, '').trim();
              });
            }
            // Transform to Invoice format
            const invoiceData = {
              invoice_id: invoiceId,
              invoice_number: finalFields.invoice_number || finalFields.invoiceNumber || selectedInvoice.docNumber || `FDOC-${invoiceId.substring(0, 8).toUpperCase()}`,
              invoice_date: finalFields.invoice_date || finalFields.date || fullFdocDoc.uploaded_at ? new Date(fullFdocDoc.uploaded_at).toISOString().split('T')[0] : '',
              customer_name: finalFields.customer_name || finalFields.customerName || finalFields.client_name || finalFields.clientName || 'Unknown Customer',
              customer_id: finalFields.customer_id || finalFields.customerId || null,
              site_id: siteId,
              site_name: siteName,
              billing_address: finalFields.billing_address || {},
              shipping_address: finalFields.shipping_address || finalFields.shippingAddress || {},
              payment_terms: finalFields.payment_terms || finalFields.paymentTerms || 'N/A',
              payment_mode: (finalFields.payment_mode || finalFields.paymentMode || '').trim() || notesPaymentMode || '',
              po_number: (finalFields.po_number || finalFields.poNumber || '').trim() || notesPo || '',
              mode_of_transaction: (finalFields.mode_of_transaction || finalFields.modeOfTransaction || '').trim() || notesModeOfTransaction || '',
              transaction_id: (finalFields.transaction_id || finalFields.transactionId || '').trim() || notesTransactionId || '',
              amount_paid: finalFields.amount_paid != null && finalFields.amount_paid !== '' ? parseFloat(finalFields.amount_paid) : (notesAmountPaid ? parseFloat(notesAmountPaid) : undefined),
              subtotal: parseFloat(finalFields.subtotal || finalFields.sub_total || 0),
              discount: parseFloat(finalFields.discount || 0),
              shipping_total: parseFloat(finalFields.shipping_total || finalFields.shipping_handling || 0),
              insurance: parseFloat(finalFields.insurance || 0),
              tax_total: parseFloat(finalFields.tax_total || finalFields.tax_amount || 0),
              grand_total: parseFloat(finalFields.grand_total || finalFields.total_amount || 0),
              status: fullFdocDoc.status || finalFields.status || 'sent',
              payment_status: fullFdocDoc.payment_status || finalFields.payment_status || 'unpaid',
              currency: finalFields.currency || 'INR',
              notes: finalFields.notes || '',
              terms_of_sale: finalFields.terms_of_sale ?? finalFields.termsOfSale ?? null,
              estimated_ship_date: fullFdocDoc.shipping_details?.estimated_ship_date ?? finalFields.estimated_ship_date ?? finalFields.estimatedShipDate ?? null,
              shipping_details: fullFdocDoc.shipping_details ?? {},
              discount_type: finalFields.discount_type ?? finalFields.discountType ?? 'percentage',
              discount_value: finalFields.discount_value ?? finalFields.discount_percentage ?? finalFields.discount_percent ?? finalFields.discountValue ?? null,
            };
            
            console.log('[Fdoc] Invoice data before mapping:', {
              invoice_number: invoiceData.invoice_number,
              customer_name: invoiceData.customer_name,
              site_name: invoiceData.site_name,
              payment_terms: invoiceData.payment_terms,
              payment_mode: invoiceData.payment_mode,
              subtotal: invoiceData.subtotal,
              tax_total: invoiceData.tax_total,
              grand_total: invoiceData.grand_total,
              lineItemsCount: lineItems.length,
              lineItems: lineItems
            });
            
            // Transform line items to the format expected by mapInvoiceToUI
            const transformedLineItems = lineItems.map((item, index) => {
              // Handle different line item formats
              if (typeof item === 'object' && item !== null) {
                return {
                  description: item.name || item.description || item.item_name || item.itemName || `Item ${index + 1}`,
                  quantity: item.quantity || item.qty || 0,
                  unit: item.units || item.unit || 'pcs',
                  unit_price: item.rate || item.unit_price || item.price || 0,
                  line_total: item.amount || item.line_total || item.lineTotal || item.total || 0,
                  line_subtotal: item.amount || item.line_total || item.lineTotal || item.total || 0,
                };
              }
              return null;
            }).filter(Boolean);
            
            console.log('[Fdoc] Transformed line items:', transformedLineItems.length, 'items', transformedLineItems);
            
            const mapped = mapInvoiceToUI(invoiceData, transformedLineItems);
            console.log('[Fdoc] Mapped Invoice from FDOC:', {
              invoiceNumber: mapped.invoiceNumber,
              client: mapped.client,
              paymentTerms: mapped.paymentTerms,
              paymentMode: mapped.paymentMode,
              siteName: mapped.siteName,
              itemsCount: mapped.lineItems?.length,
              items: mapped.lineItems,
              subtotal: mapped.subtotal,
              taxTotal: mapped.totalTax,
              grandTotal: mapped.grandTotal
            });
            
            setInvoiceDetails(prev => ({
              ...prev,
              [invoiceId]: mapped,
            }));
            setSelectedInvoice(mapped);
          } else {
            console.warn('[Fdoc] No extracted_data.fields found in FDOC document');
          }
        }
      } catch (err) {
        console.error('[Fdoc] Error fetching Invoice details:', err);
        const isFileMissing = err.status === 410 || err.fileMissing === true;
        if (isFileMissing && selectedInvoice) {
          const fdocId = selectedInvoice.fdoc_id || selectedInvoice.id;
          await autoCancelMissingFdoc(fdocId, 'file missing (410), auto-deleted when opening Invoice');
          setDocuments((prev) => prev.filter((d) => (d.fdoc_id || d.id) !== fdocId));
          setSelectedInvoice(null);
          setInvoiceDetails((prev) => {
            const next = { ...prev };
            delete next[fdocId];
            return next;
          });
        }
      } finally {
        setIsLoadingInvoiceDetails(false);
        fetchingInvoiceRef.current = false;
      }
    };

    fetchInvoiceDetails();
  }, [selectedInvoice?.id, selectedInvoice?.fdoc_id, sites]);

  // Fetch Bill details when a card is selected
  useEffect(() => {
    const fetchBillDetails = async () => {
      console.log('[Fdoc] Bill details useEffect triggered:', {
        hasSelectedBill: !!selectedBill,
        selectedBill: selectedBill,
        billId: selectedBill?.id || selectedBill?.billId
      });
      
      if (!selectedBill) {
        console.log('[Fdoc] No selectedBill, returning');
        return;
      }

      const billId = selectedBill.id || selectedBill.billId;
      if (!billId) {
        console.log('[Fdoc] No billId found, returning');
        return;
      }

      if (selectedBill.fileMissing || selectedBill.apiStatus === 'file_missing') {
        setIsLoadingBillDetails(false);
        return;
      }
      
      console.log('[Fdoc] Starting Bill details fetch for:', billId);

      // Check if we already have the details cached
      if (billDetails[billId] && (billDetails[billId].billNumber || billDetails[billId].orderNumber)) {
        console.log('[Fdoc] Using cached Bill details');
        setSelectedBill(billDetails[billId]);
        return;
      }

      // Check if we're already fetching this bill to prevent duplicate calls
      if (fetchingBillRef.current) {
        console.log('[Fdoc] Already fetching Bill details, skipping duplicate call');
        return;
      }

      fetchingBillRef.current = true;
      setIsLoadingBillDetails(true);
      try {
        const isExternalDoc = selectedBill.externalDoc === true;

        console.log('[Fdoc] Processing Bill document:', {
          billId: billId,
          isExternalDoc: isExternalDoc,
          hasOriginalDoc: !!selectedBill.originalDoc,
          selectedBillKeys: Object.keys(selectedBill)
        });

        // Always try to fetch from Bill API if we don't have line items
        // Bills from Bill API need to be fetched to get full details with line items
        const hasLineItems = (selectedBill.originalDoc?.line_items && 
                             Array.isArray(selectedBill.originalDoc.line_items) && 
                             selectedBill.originalDoc.line_items.length > 0) ||
                            (selectedBill.items && Array.isArray(selectedBill.items) && selectedBill.items.length > 0);
        
        if (!hasLineItems && billId) {
          console.log('[Fdoc] No line items found, fetching Bill from API');
          try {
            const result = await billApi.getBill(billId);
            console.log('[Fdoc] Bill API response:', {
              success: result.success,
              hasBill: !!result.bill,
              billKeys: result.bill ? Object.keys(result.bill) : [],
              hasLineItems: !!(result.bill?.line_items && result.bill.line_items.length > 0),
              lineItemsCount: result.bill?.line_items?.length || 0,
              bill: result.bill
            });
            
            if (result.success && result.bill) {
              const mapped = mapBillToUI(result.bill, sites);
              console.log('[Fdoc] Mapped Bill from API:', {
                billNumber: mapped.billNumber,
                orderNumber: mapped.orderNumber,
                customerName: mapped.customerName,
                siteName: mapped.siteName,
                tableNumber: mapped.tableNumber,
                itemsCount: mapped.items?.length,
                grandTotal: mapped.grandTotal
              });
              setBillDetails(prev => ({
                ...prev,
                [billId]: mapped,
              }));
              setSelectedBill(mapped);
              return;
            } else {
              console.error('[Fdoc] Failed to fetch Bill from API:', result);
              // Fall through to use originalDoc or card data
            }
          } catch (apiErr) {
            console.error('[Fdoc] Error fetching Bill from API:', apiErr);
            // Fall through to use originalDoc or card data
          }
        } else if (hasLineItems && selectedBill.originalDoc) {
          // We have line items in originalDoc, use it directly
          console.log('[Fdoc] Using originalDoc with line items');
          const mapped = mapBillToUI(selectedBill.originalDoc, sites);
          console.log('[Fdoc] Mapped Bill from originalDoc:', {
            billNumber: mapped.billNumber,
            orderNumber: mapped.orderNumber,
            customerName: mapped.customerName,
            siteName: mapped.siteName,
            tableNumber: mapped.tableNumber,
            itemsCount: mapped.items?.length,
            grandTotal: mapped.grandTotal
          });
          setBillDetails(prev => ({
            ...prev,
            [billId]: mapped,
          }));
          setSelectedBill(mapped);
          return;
        }
        
        // If API call failed or we don't have originalDoc, use card data or originalDoc
        console.log('[Fdoc] Using Bill data from originalDoc or card (fallback)');
        console.log('[Fdoc] selectedBill structure:', {
          keys: Object.keys(selectedBill),
          docNumber: selectedBill.docNumber,
          billNumber: selectedBill.billNumber,
          orderNumber: selectedBill.orderNumber,
          hasOriginalDoc: !!selectedBill.originalDoc,
          originalDocKeys: selectedBill.originalDoc ? Object.keys(selectedBill.originalDoc) : [],
          originalDocLineItems: selectedBill.originalDoc?.line_items?.length || 0
        });
        
        // Get billNumber and orderNumber from originalDoc if available
        const originalDoc = selectedBill.originalDoc || {};
        console.log('[Fdoc] originalDoc details:', {
          bill_number: originalDoc.bill_number,
          order_id: originalDoc.order_id,
          table_number: originalDoc.table_number,
          site_id: originalDoc.site_id,
          customer: originalDoc.customer,
          line_items: originalDoc.line_items?.length || 0,
          subtotal: originalDoc.subtotal,
          total: originalDoc.total
        });
        
        const mapped = {
          id: billId,
          billId: billId,
          billNumber: originalDoc.bill_number || selectedBill.billNumber || selectedBill.docNumber || `BILL-${billId.substring(0, 8).toUpperCase()}`,
          orderNumber: originalDoc.order_id || selectedBill.orderNumber || originalDoc.orderNumber || '',
          date: selectedBill.date || (originalDoc.created_at ? normalizeDate(originalDoc.created_at) : ''),
          tableNumber: originalDoc.table_number || selectedBill.tableNumber || '',
          siteId: originalDoc.site_id || selectedBill.siteId || null,
          siteName: originalDoc.site_name || selectedBill.siteName || (originalDoc.site_id ? (sites.find(s => resolveSiteValue(s) === originalDoc.site_id)?.site_name || 'N/A') : 'N/A'),
          customerName: originalDoc.customer?.name || selectedBill.customerName || '',
          customerMobile: originalDoc.customer?.mobile || selectedBill.customerMobile || '',
          customerEmail: originalDoc.customer?.email || selectedBill.customerEmail || '',
          items: (originalDoc.line_items || selectedBill.items || []).map(item => ({
            name: item.item_name || item.name || item.description || '',
            quantity: item.quantity || 0,
            unit: item.unit || 'pcs',
            rate: item.unit_price || item.rate || 0,
            amount: item.line_total || item.amount || 0,
          })),
          subtotal: originalDoc.subtotal || selectedBill.subtotal || selectedBill.totalAmount || 0,
          discountCode: originalDoc.discount_code || selectedBill.discountCode || '',
          discountValue: originalDoc.discount_amount || selectedBill.discountValue || selectedBill.discountAmount || 0,
          discountedSubtotal: originalDoc.discounted_subtotal || selectedBill.discountedSubtotal || (originalDoc.subtotal || selectedBill.subtotal || selectedBill.totalAmount || 0) - (originalDoc.discount_amount || selectedBill.discountValue || selectedBill.discountAmount || 0),
          cgstRate: originalDoc.cgst_rate || selectedBill.cgstRate || 0,
          sgstRate: originalDoc.sgst_rate || selectedBill.sgstRate || 0,
          cgstAmount: originalDoc.cgst || selectedBill.cgstAmount || 0,
          sgstAmount: originalDoc.sgst || selectedBill.sgstAmount || 0,
          grandTotal: originalDoc.total || originalDoc.grand_total || selectedBill.grandTotal || selectedBill.totalAmount || 0,
        };
        console.log('[Fdoc] Mapped Bill from card/originalDoc data:', {
          billNumber: mapped.billNumber,
          orderNumber: mapped.orderNumber,
          customerName: mapped.customerName,
          siteName: mapped.siteName,
          tableNumber: mapped.tableNumber,
          itemsCount: mapped.items?.length,
          grandTotal: mapped.grandTotal,
          fullMapped: mapped
        });
        setBillDetails(prev => ({
          ...prev,
          [billId]: mapped,
        }));
        setSelectedBill(mapped);
      } catch (err) {
        console.error('[Fdoc] Error fetching Bill details:', err);
        const isFileMissing = err.status === 410 || err.fileMissing === true;
        if (isFileMissing && selectedBill) {
          const fdocId = selectedBill.fdoc_id || selectedBill.id || selectedBill.billId;
          await autoCancelMissingFdoc(fdocId, 'file missing (410), auto-deleted when opening Bill');
          setDocuments((prev) => prev.filter((d) => (d.fdoc_id || d.id) !== fdocId));
          setSelectedBill(null);
          setBillDetails((prev) => {
            const next = { ...prev };
            delete next[fdocId];
            return next;
          });
        } else {
          // On other errors, use card data as fallback
          const fallbackBill = {
            id: billId,
            billId: billId,
            billNumber: selectedBill.billNumber || selectedBill.docNumber || `BILL-${billId.substring(0, 8).toUpperCase()}`,
            orderNumber: selectedBill.orderNumber || '',
            date: selectedBill.date || '',
            siteId: selectedBill.siteId || null,
            siteName: selectedBill.siteName || 'N/A',
            customerName: selectedBill.customerName || '',
            items: [],
            subtotal: selectedBill.totalAmount || 0,
            grandTotal: selectedBill.totalAmount || 0,
          };
          setSelectedBill(fallbackBill);
        }
      } finally {
        setIsLoadingBillDetails(false);
        fetchingBillRef.current = false;
      }
    };

    fetchBillDetails();
  }, [selectedBill?.id, selectedBill?.billId, sites]);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <h1 className={typography.h2} style={{ color: '#000000' }}>
                  Fdoc
                </h1>
                <IconTextButton
                  icon={<Plus className="w-7 h-7 text-white" />}
                  text="Upload Fdoc"
                  onClick={() => {
                    openOverlay('fdocIn', {
                      onCollect: async (data) => {
                        console.log('Fdoc upload data:', data);
                        // Refresh documents list after upload
                        if (selectedFdocType) {
                          // Trigger refetch by updating a dependency
                          setSelectedFdocType(selectedFdocType);
                        }
                      }
                    });
                  }}
                  variant="default"
                  size="md"
                />
              </div>
            </div>
            <p className="mt-2 text-gray-600">
              View and manage Fdoc documents
            </p>
          </div>

          {/* Filters (single-line bar) */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 flex-wrap overflow-visible pb-1">
            <div className="w-full sm:w-auto sm:min-w-[220px]">
              <label
                htmlFor="fdoc-order-history-kind"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <FileText className="w-4 h-4" />
                Document Party
              </label>
              <select
                id="fdoc-order-history-kind"
                value={orderHistoryKind}
                onChange={(e) => {
                  const nextKind = e.target.value;
                  setOrderHistoryKind(nextKind);
                  // Keep party-id filter in sync with selected party kind.
                  if (nextKind !== 'vendor') setSelectedOrderHistoryVendorId('');
                  if (nextKind !== 'customer') setSelectedOrderHistoryCustomerId('');
                }}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="vendor">Vendor</option>
                <option value="customer">Customer</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div className="w-full sm:w-auto sm:min-w-[260px]">
              <label
                htmlFor="fdoc-order-history-party-id"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <FileText className="w-4 h-4" />
                {effectiveOrderHistoryKind === 'customer' ? 'Customer ID' : 'Vendor ID'}
              </label>
              <select
                id="fdoc-order-history-party-id"
                value={
                  effectiveOrderHistoryKind === 'customer'
                    ? selectedOrderHistoryCustomerId
                    : selectedOrderHistoryVendorId
                }
                onChange={(e) => {
                  if (effectiveOrderHistoryKind === 'customer') {
                    setSelectedOrderHistoryCustomerId(e.target.value);
                  } else {
                    setSelectedOrderHistoryVendorId(e.target.value);
                  }
                }}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                disabled={orderHistoryFiltersLoading || orderHistoryKind === 'both'}
              >
                <option value="">
                  {orderHistoryKind === 'both'
                    ? 'Select party first'
                    : orderHistoryFiltersLoading
                      ? `Loading ${effectiveOrderHistoryKind === 'customer' ? 'customers' : 'vendors'}...`
                      : `All ${effectiveOrderHistoryKind === 'customer' ? 'customers' : 'vendors'}`}
                </option>
                {(effectiveOrderHistoryKind === 'customer'
                  ? orderHistoryFilters.customers || []
                  : orderHistoryFilters.vendors || []
                ).map((party) => {
                  const optionValue =
                    effectiveOrderHistoryKind === 'customer'
                      ? encodeCustomerFilterValue(party)
                      : encodeVendorFilterValue(party);
                  const partyId =
                    effectiveOrderHistoryKind === 'customer' ? party.customer_id : party.vendor_id;
                  const partyName =
                    effectiveOrderHistoryKind === 'customer' ? party.customer_name : party.vendor_name;
                  return (
                    <option key={optionValue || partyName || partyId} value={optionValue}>
                      {partyId || partyName || 'Unknown'} {partyName && partyId ? `- ${partyName}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Fdoc Type */}
            <div className="w-full sm:w-auto sm:min-w-[240px]">
              <label
                htmlFor="fdoc-type"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <FileText className="w-4 h-4" />
                Fdoc Type
              </label>
              <select
                id="fdoc-type"
                value={selectedFdocType}
                onChange={(e) => {
                  const selectedType = e.target.value;
                  setSelectedFdocType(selectedType);
                  
                  // Invoice, Purchase Order, Estimation, and Bill are now handled in the same page - no navigation needed
                }}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="">Select Fdoc type</option>
                {fdocTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Site */}
            <div className="w-full sm:w-auto sm:min-w-[240px]">
              <label
                htmlFor="fdoc-site"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <MapPin className="w-4 h-4" />
                Select Site
              </label>
              <select
                id="fdoc-site"
                value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                onChange={(e) => {
                  const siteId = e.target.value;
                  const site = sites.find(s => resolveSiteValue(s) === siteId);
                  setSelectedSite(site || null);
                }}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                required={selectedFdocType !== FDOC_ORDER_HISTORY_TYPE}
                disabled={isSitesLoading || selectedFdocType === 'Bill' || selectedFdocType === 'Bill OO'}
              >
                <option value="">
                  {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                </option>
                {sites.map((site) => (
                  <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                    {site.site_name || site.name || 'Unknown Site'}
                  </option>
                ))}
              </select>
            </div>

            {/* Month */}
            <div className="w-full sm:w-auto sm:min-w-[200px]">
              <label
                htmlFor="fdoc-month"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <Calendar className="w-4 h-4" />
                Select Month
              </label>
              <input
                id="fdoc-month"
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>

          </div>

          <div className="mb-6 mt-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                checked={esignedOnly}
                onChange={(e) => setEsignedOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-200"
              />
              E-signed only
            </label>
          </div>



          {selectedFdocType === FDOC_ORDER_HISTORY_TYPE && (
            <div className="mb-6 space-y-4 rounded-2xl border border-gray-200 bg-gray-50/80 p-4 md:p-6">
              <p className="text-sm font-semibold text-gray-800">
                FDOC order history filters
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {orderHistoryKind === 'both' && (
                  <div>
                    <label
                      htmlFor="fdoc-oh-list-kind"
                      className="mb-2 block text-sm font-semibold text-gray-700"
                    >
                      List view
                    </label>
                    <select
                      id="fdoc-oh-list-kind"
                      value={orderHistoryListKind}
                      onChange={(e) => setOrderHistoryListKind(e.target.value)}
                      className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    >
                      <option value="vendor">Vendor (PO / Estimation)</option>
                      <option value="customer">Customer (Invoice / Bill)</option>
                    </select>
                  </div>
                )}
                {effectiveOrderHistoryKind === 'vendor' && (
                  <div>
                    <label
                      htmlFor="fdoc-oh-vendor"
                      className="mb-2 block text-sm font-semibold text-gray-700"
                    >
                      Vendor
                    </label>
                    <select
                      id="fdoc-oh-vendor"
                      value={selectedOrderHistoryVendorId}
                      onChange={(e) => setSelectedOrderHistoryVendorId(e.target.value)}
                      className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      disabled={orderHistoryFiltersLoading}
                    >
                      <option value="">
                        {orderHistoryFiltersLoading ? 'Loading vendors...' : 'All vendors'}
                      </option>
                      {(orderHistoryFilters.vendors || []).map((v) => (
                        <option
                          key={encodeVendorFilterValue(v) || v.vendor_name}
                          value={encodeVendorFilterValue(v)}
                        >
                          {v.vendor_name || v.vendor_id || 'Vendor'}{' '}
                          {v.count != null ? `(${v.count})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {effectiveOrderHistoryKind === 'customer' && (
                  <div>
                    <label
                      htmlFor="fdoc-oh-customer"
                      className="mb-2 block text-sm font-semibold text-gray-700"
                    >
                      Customer
                    </label>
                    <select
                      id="fdoc-oh-customer"
                      value={selectedOrderHistoryCustomerId}
                      onChange={(e) => setSelectedOrderHistoryCustomerId(e.target.value)}
                      className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      disabled={orderHistoryFiltersLoading}
                    >
                      <option value="">
                        {orderHistoryFiltersLoading ? 'Loading customers...' : 'All customers'}
                      </option>
                      {(orderHistoryFilters.customers || []).map((c) => (
                        <option
                          key={encodeCustomerFilterValue(c) || c.customer_name}
                          value={encodeCustomerFilterValue(c)}
                        >
                          {c.customer_name || c.customer_id || 'Customer'}{' '}
                          {c.count != null ? `(${c.count})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {orderHistoryFiltersLoading && (
                <p className="text-xs text-gray-500">Loading party filter options...</p>
              )}
              {orderHistoryFiltersError && !orderHistoryFiltersLoading && (
                <p className="text-xs text-red-600">{orderHistoryFiltersError}</p>
              )}
              <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                <span>
                  Total amount (filtered):{' '}
                  <strong>{formatCurrency(orderHistorySummary.total_amount_sum ?? 0)}</strong>
                </span>
                <span>
                  Tax total:{' '}
                  <strong>{formatCurrency(orderHistorySummary.tax_total_sum ?? 0)}</strong>
                </span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-semibold">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Show InvoiceView when Invoice or Invoice OO is selected */}
          {selectedFdocType === 'Invoice' || selectedFdocType === 'Invoice OO' ? (
            <div className="mt-6">
              <InvoiceView 
                externalSite={selectedSite}
                externalMonth={selectedMonth}
                externalSites={sites}
                hideSelectors={true}
                fetchFromFdoc={selectedFdocType === 'Invoice OO'}
                fdocType={selectedFdocType}
                esignedOnly={esignedOnly}
              />
            </div>
          ) : selectedFdocType === 'Purchase Order' || selectedFdocType === 'Purchase Order OO' ? (
            <div className="mt-6">
              <PurchaseOrderView 
                externalSite={selectedSite}
                externalMonth={selectedMonth}
                externalSites={sites}
                hideSelectors={true}
                fetchFromFdoc={selectedFdocType === 'Purchase Order OO'}
                esignedOnly={esignedOnly}
              />
            </div>
          ) : selectedFdocType === 'Estimation' || selectedFdocType === 'Estimation OO' ? (
            /* Show QuotationView when Estimation/Quotation or Estimation OO is selected */
            <div className="mt-6">
              <QuotationView 
                externalSite={selectedSite}
                externalMonth={selectedMonth}
                externalSites={sites}
                hideSelectors={true}
                fetchFromFdoc={selectedFdocType === 'Estimation OO'}
                esignedOnly={esignedOnly}
              />
            </div>
          ) : selectedFdocType === 'Bill' || selectedFdocType === 'Bill OO' ? (
            <div className="mt-6">
              <BillCustomerView
                externalSite={selectedSite}
                externalMonth={selectedMonth}
                hideSelectors={true}
                fetchFromFdoc={selectedFdocType === 'Bill OO'}
                esignedOnly={esignedOnly}
              />
            </div>
          ) : (
            /* Cards Section for other document types */
            selectedMonth && selectedFdocType ? (
            <>
            <div className="rounded-3xl">
              <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[220px]">File name</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">Doc#</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Date</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">Site</th>
                      <th className="px-4 py-3 font-semibold min-w-[110px]">Version</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Total</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Tax</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Status</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">Type</th>
                      <th className="px-4 py-3 font-semibold min-w-[200px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr className="border-t border-gray-100">
                        <td className="px-4 py-6 text-gray-600" colSpan={11}>
                          <div className="flex items-center justify-center gap-3">
                            <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                            <span>Loading documents…</span>
                          </div>
                        </td>
                      </tr>
                    ) : searchedData.length > 0 ? (
                      searchedData.map((item, index) => {
                        const id = item.fdoc_id || item.id;
                        const o = item.originalDoc || {};
                        const filename =
                          item.file_name ||
                          o.file_name ||
                          item.fileName ||
                          o.fileName ||
                          '—';
                        const siteLabel = item.siteName || item.site_name || o.site_name || '—';
                        const isMissing = item.fileMissing || item.apiStatus === 'file_missing';
                        const versionNo =
                          item.version_no ??
                          item.versionNo ??
                          item.latest_version_no ??
                          o.version_no ??
                          o.versionNo ??
                          o.latest_version_no ??
                          null;

                        const statusClass = isMissing
                          ? 'bg-amber-100 text-amber-800'
                          : item.status === 'Active' || item.status === 'Completed'
                            ? 'bg-green-100 text-green-700'
                            : item.status === 'Processing'
                              ? 'bg-blue-100 text-blue-700'
                              : item.status === 'Failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700';

                        return (
                          <tr
                            key={id || `fdoc-row-${index}`}
                            className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                            onClick={() => {
                              openOverlay('fdocInPreview', { documentData: item });
                            }}
                          >
                            <td className="px-4 py-4 align-top text-gray-800">{index + 1}</td>
                            <td className="px-4 py-4 align-top text-gray-800">
                              <div className="max-w-[340px] truncate" title={filename}>
                                {filename}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-800">
                              <div className="max-w-[220px] truncate" title={item.docNumber || '—'}>
                                {item.docNumber || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-700">
                              {item.date
                                ? new Date(item.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: '2-digit',
                                  })
                                : '—'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-700">
                              <div className="max-w-[260px] truncate" title={siteLabel}>
                                {siteLabel}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-800">
                              {versionNo != null && versionNo !== '' ? `v${versionNo}` : '—'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-800">
                              {item.totalAmount != null ? formatCurrency(item.totalAmount) : '—'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-800">
                              {item.taxAmount != null ? formatCurrency(item.taxAmount) : '—'}
                            </td>
                            <td className="px-4 py-4 align-top">
                              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                                {isMissing ? 'File missing' : item.status || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-700">
                              <div className="max-w-[220px] truncate" title={item.documentType || selectedFdocType}>
                                {item.documentType || selectedFdocType || '—'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openOverlay('fdocInPreview', { documentData: item });
                                  }}
                                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700"
                                >
                                  View
                                </button>

                                {(selectedFdocType === 'Amenity Booking Invoice') && !isMissing && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInvoicePdfDownload(item);
                                    }}
                                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                                  >
                                    <Download className="h-4 w-4 text-white stroke-white" strokeWidth={2} />
                                    Download
                                  </button>
                                )}

                                {isMissing && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelFdoc(id);
                                    }}
                                    disabled={cancellingFdocId === id}
                                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                                  >
                                    {cancellingFdocId === id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                    {cancellingFdocId === id ? 'Deleting…' : 'Delete'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr className="border-t border-gray-100">
                        <td className="px-4 py-6 text-center text-gray-500" colSpan={11}>
                          {cardSearch.trim()
                            ? 'No Fdoc documents match your search.'
                            : 'No Fdoc documents found for the selected filters.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {selectedFdocType === FDOC_ORDER_HISTORY_TYPE && orderHistoryLastKey && (
              <div className="mt-6 flex w-full justify-center">
                <button
                  type="button"
                  onClick={loadMoreOrderHistory}
                  disabled={orderHistoryLoadingMore}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-6 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
                >
                  {orderHistoryLoadingMore ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                      Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            )}
            </>
          ) : (
            <Card.Light className="p-8 text-center">
              <p className="text-gray-500 text-lg">
                Please select both month and Fdoc type to view documents.
              </p>
            </Card.Light>
          )
          )}
        </motion.div>
      </div>

      {/* Upload Fdoc Overlay - Now managed by OverlayContext */}

      {/* Purchase Order Detail Modal */}
      {selectedPurchaseOrder &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    Purchase Order Details
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPurchaseOrder(null);
                        setPurchaseOrderDetails({});
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    {!selectedPurchaseOrder.fileMissing && (
                      <button
                        type="button"
                        onClick={() => handlePurchaseOrderDownload(selectedPurchaseOrder)}
                        className="rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                          Download PDF
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {selectedPurchaseOrder.fileMissing || selectedPurchaseOrder.apiStatus === 'file_missing' ? (
                    <div className="py-8 px-4 rounded-xl bg-amber-50 border border-amber-200" role="alert">
                      <p className="text-amber-800 font-semibold">Source file is no longer available in storage.</p>
                      <p className="text-amber-700 mt-2">Please re-upload the document.</p>
                      <button
                        type="button"
                        onClick={() => handleCancelFdoc(selectedPurchaseOrder.fdoc_id || selectedPurchaseOrder.id)}
                        disabled={!!cancellingFdocId}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {cancellingFdocId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {cancellingFdocId ? 'Deleting...' : 'Delete entry'}
                      </button>
                    </div>
                  ) : isLoadingPurchaseOrderDetails ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
                      <p className="text-gray-500">Loading purchase order details...</p>
                    </div>
                  ) : selectedPurchaseOrder && selectedPurchaseOrder.poNumber ? (
                    <>
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-semibold text-gray-900">{selectedPurchaseOrder.poNumber}</h2>
                          <p className="text-gray-500 mt-1">{selectedPurchaseOrder.vendor}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">{selectedPurchaseOrder.poDate}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">PO Information</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">PO Date:</span>
                              <span className="font-medium text-gray-700">{selectedPurchaseOrder.poDate || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Expected Delivery:</span>
                              <span className="font-medium text-gray-700">{selectedPurchaseOrder.expectedDeliveryDate || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Payment Date:</span>
                              <span className="font-medium text-gray-700">{selectedPurchaseOrder.paymentDate || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Payment Mode:</span>
                              <span className="font-medium text-gray-700">{selectedPurchaseOrder.paymentMode || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Site:</span>
                              <span className="font-medium text-gray-700">{selectedPurchaseOrder.siteName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Status:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(selectedPurchaseOrder.status)}`}>
                                {selectedPurchaseOrder.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h3>
                          <div className="space-y-1 text-sm text-gray-700">
                            <p>{selectedPurchaseOrder.billingAddress?.firstName} {selectedPurchaseOrder.billingAddress?.lastName}</p>
                            <p>{selectedPurchaseOrder.billingAddress?.email || 'N/A'}</p>
                            <p>{selectedPurchaseOrder.billingAddress?.address || 'N/A'}</p>
                            <p>{selectedPurchaseOrder.billingAddress?.city}, {selectedPurchaseOrder.billingAddress?.state}</p>
                            <p>{selectedPurchaseOrder.billingAddress?.country} - {selectedPurchaseOrder.billingAddress?.pinCode}</p>
                            <p className="mt-2">Mobile: {selectedPurchaseOrder.billingAddress?.mobileNumber || 'N/A'}</p>
                            <p>PAN: {selectedPurchaseOrder.billingAddress?.panNumber || 'N/A'}</p>
                            <p>GST: {selectedPurchaseOrder.billingAddress?.gstNumber || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Items</h3>
                        {selectedPurchaseOrder.items && selectedPurchaseOrder.items.length > 0 ? (
                          <div className="overflow-x-auto rounded-2xl border border-gray-200">
                            <table className="min-w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-left text-gray-600">
                                  <th className="px-4 py-3 font-semibold">S.No</th>
                                  <th className="px-4 py-3 font-semibold">Name</th>
                                  <th className="px-4 py-3 font-semibold">HSN Code</th>
                                  <th className="px-4 py-3 font-semibold">Units</th>
                                  <th className="px-4 py-3 font-semibold">Rate</th>
                                  <th className="px-4 py-3 font-semibold">Tax (%)</th>
                                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedPurchaseOrder.items.map((item, index) => (
                                  <tr key={index} className="border-t border-gray-100">
                                    <td className="px-4 py-3">{index + 1}</td>
                                    <td className="px-4 py-3">{item.name}</td>
                                    <td className="px-4 py-3">{item.hsnCode || 'N/A'}</td>
                                    <td className="px-4 py-3">{item.units}</td>
                                    <td className="px-4 py-3">₹{(item.rate || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3">{item.tax || 0}%</td>
                                    <td className="px-4 py-3 text-right">₹{(item.amount || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                                  <td colSpan={6} className="px-4 py-3 font-semibold">Subtotal</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{(selectedPurchaseOrder.subtotal || 0).toFixed(2)}</td>
                                </tr>
                                <tr className="border-t border-gray-100 bg-white text-gray-700">
                                  <td colSpan={6} className="px-4 py-3 font-semibold">Discount</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{(selectedPurchaseOrder.discount || 0).toFixed(2)}</td>
                                </tr>
                                <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                                  <td colSpan={6} className="px-4 py-3">Grand Total</td>
                                  <td className="px-4 py-3 text-right">₹{(selectedPurchaseOrder.grandTotal || 0).toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">No items found</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No purchase order data available</p>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      {/* Invoice Detail Modal */}
      {selectedInvoice &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    Invoice Details
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedInvoice(null);
                        setInvoiceDetails({});
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInvoicePdfDownload(selectedInvoice)}
                      className="rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                        Download PDF
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {selectedInvoice && (selectedInvoice.fileMissing || selectedInvoice.apiStatus === 'file_missing') ? (
                    <div className="py-8 px-4 rounded-xl bg-amber-50 border border-amber-200" role="alert">
                      <p className="text-amber-800 font-semibold">Source file is no longer available in storage.</p>
                      <p className="text-amber-700 mt-2">Please re-upload the document.</p>
                      <button
                        type="button"
                        onClick={() => handleCancelFdoc(selectedInvoice.fdoc_id || selectedInvoice.id)}
                        disabled={!!cancellingFdocId}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {cancellingFdocId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {cancellingFdocId ? 'Deleting...' : 'Delete entry'}
                      </button>
                    </div>
                  ) : isLoadingInvoiceDetails ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
                      <p className="text-gray-500">Loading invoice details...</p>
                    </div>
                  ) : selectedInvoice && selectedInvoice.invoiceNumber ? (
                    <>
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-semibold text-gray-900">{selectedInvoice.invoiceNumber}</h2>
                          <p className="text-gray-500 mt-1">{selectedInvoice.client}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">{selectedInvoice.date}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Invoice Date:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.date || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Due Date:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.dueDate || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Payment Terms:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.paymentTerms || 'N/A'}</span>
                            </div>
                            {selectedInvoice.poNumber && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">PO Number:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.poNumber}</span>
                            </div>
                            )}
                            {selectedInvoice.paymentMode && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Payment Mode:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.paymentMode}</span>
                            </div>
                            )}
                            {selectedInvoice.modeOfTransaction && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Mode of transaction:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.modeOfTransaction}</span>
                            </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-500">Site:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.siteName || 'N/A'}</span>
                            </div>
                            {(selectedInvoice.estimatedShipDate != null && selectedInvoice.estimatedShipDate !== '') && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Est. Ship Date:</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.estimatedShipDate}</span>
                            </div>
                            )}
                            {(selectedInvoice.discountValue != null && selectedInvoice.discountValue !== '') && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Discount ({selectedInvoice.discountType === 'amount' ? 'Amount' : '%'}):</span>
                              <span className="font-medium text-gray-700">
                                {selectedInvoice.discountType === 'amount' ? `₹${Number(selectedInvoice.discountValue || 0).toFixed(2)}` : `${selectedInvoice.discountValue}%`}
                              </span>
                            </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                          <div className="space-y-1 text-sm text-gray-700">
                            <p className="font-medium">{selectedInvoice.client || 'Unknown Customer'}</p>
                            <p className="text-gray-500">Billing Address:</p>
                            <p className="text-gray-600">{selectedInvoice.billingAddress || 'N/A'}</p>
                            {selectedInvoice.shippingAddress && selectedInvoice.shippingAddress !== 'N/A' && (
                              <>
                                <p className="text-gray-500 mt-2">Shipping Address:</p>
                                <p className="text-gray-600">{selectedInvoice.shippingAddress}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment and transaction details</h3>
                        <div className="space-y-2 text-sm rounded-xl border border-gray-200 p-4 bg-gray-50/80">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${
                              selectedInvoice.status === 'Paid' ? 'bg-green-100 text-green-700 border-green-200' :
                              selectedInvoice.status === 'Pending' || selectedInvoice.status === 'Sent' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                              selectedInvoice.status === 'Overdue' ? 'bg-red-100 text-red-700 border-red-200' :
                              'bg-gray-100 text-gray-700 border-gray-200'
                            }`}>
                              {selectedInvoice.status || 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Transaction ID</span>
                            <span className="font-medium text-gray-700">{selectedInvoice.transactionId || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Amount paid</span>
                            <span className="font-medium text-gray-700">
                              {selectedInvoice.amountPaid != null && selectedInvoice.amountPaid !== ''
                                ? (typeof selectedInvoice.amountPaid === 'number'
                                    ? `₹${Number(selectedInvoice.amountPaid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : selectedInvoice.amountPaid)
                                : '—'}
                            </span>
                          </div>
                          {(selectedInvoice.paymentMode) && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Payment mode</span>
                              <span className="font-medium text-gray-700">{selectedInvoice.paymentMode}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                        {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 ? (
                          <div className="overflow-x-auto rounded-2xl border border-gray-200">
                            <table className="min-w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-left text-gray-600">
                                  <th className="px-4 py-3 font-semibold">S.No</th>
                                  <th className="px-4 py-3 font-semibold">Description</th>
                                  <th className="px-4 py-3 font-semibold">Quantity</th>
                                  <th className="px-4 py-3 font-semibold">Units</th>
                                  <th className="px-4 py-3 font-semibold">Rate</th>
                                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedInvoice.lineItems.map((item, index) => (
                                  <tr key={index} className="border-t border-gray-100">
                                    <td className="px-4 py-3">{index + 1}</td>
                                    <td className="px-4 py-3">{item.name || 'N/A'}</td>
                                    <td className="px-4 py-3">{item.quantity || 0}</td>
                                    <td className="px-4 py-3">{item.units || 'N/A'}</td>
                                    <td className="px-4 py-3">₹{((item.rate || 0)).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right">₹{((item.amount || 0)).toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                                  <td colSpan={5} className="px-4 py-3 font-semibold">Subtotal</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{((selectedInvoice.subtotal || 0)).toFixed(2)}</td>
                                </tr>
                                {((selectedInvoice.discount || 0)) > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3 font-semibold">Discount</td>
                                    <td className="px-4 py-3 text-right font-semibold">₹{((selectedInvoice.discount || 0)).toFixed(2)}</td>
                                  </tr>
                                )}
                                {((selectedInvoice.shippingHandling || 0)) > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3 font-semibold">Shipping & Handling</td>
                                    <td className="px-4 py-3 text-right font-semibold">₹{((selectedInvoice.shippingHandling || 0)).toFixed(2)}</td>
                                  </tr>
                                )}
                                {((selectedInvoice.totalTax || 0)) > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3 font-semibold">Tax</td>
                                    <td className="px-4 py-3 text-right font-semibold">₹{((selectedInvoice.totalTax || 0)).toFixed(2)}</td>
                                  </tr>
                                )}
                                <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                                  <td colSpan={5} className="px-4 py-3">Grand Total</td>
                                  <td className="px-4 py-3 text-right">₹{((selectedInvoice.grandTotal || 0)).toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">No line items found</p>
                        )}
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Notes & Terms of Sale</h3>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          {(() => {
                            const sn = selectedInvoice.specialNotes;
                            const tn = selectedInvoice.termsOfSale ?? selectedInvoice.terms_of_sale;
                            const split = splitNotesAndTerms(selectedInvoice.notes || '', tn ?? '');
                            const specialNotes = (sn != null && sn !== '') ? sn : split.specialNotes;
                            const termsOfSale = (tn != null && tn !== '') ? tn : split.termsOfSale;
                            return (
                              <>
                                <div className="rounded-xl border-2 border-gray-200 bg-white p-4 shadow-sm">
                                  <h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-2">Special Notes</h4>
                                  <p className="text-sm text-gray-600 whitespace-pre-line min-h-[4rem]">{specialNotes || '—'}</p>
                                </div>
                                <div className="rounded-xl border-2 border-gray-200 bg-white p-4 shadow-sm">
                                  <h4 className="text-sm font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-2">Terms of Sale</h4>
                                  <p className="text-sm text-gray-600 whitespace-pre-line min-h-[4rem]">{termsOfSale || '—'}</p>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No invoice data available</p>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}

      {/* Bill Detail Modal */}
      {selectedBill &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    Bill Details
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBill(null);
                        setBillDetails({});
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBillPdfDownload(selectedBill)}
                      className="rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                        Download PDF
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {selectedBill && (selectedBill.fileMissing || selectedBill.apiStatus === 'file_missing') ? (
                    <div className="py-8 px-4 rounded-xl bg-amber-50 border border-amber-200" role="alert">
                      <p className="text-amber-800 font-semibold">Source file is no longer available in storage.</p>
                      <p className="text-amber-700 mt-2">Please re-upload the document.</p>
                      <button
                        type="button"
                        onClick={() => handleCancelFdoc(selectedBill.fdoc_id || selectedBill.id || selectedBill.billId)}
                        disabled={!!cancellingFdocId}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {cancellingFdocId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {cancellingFdocId ? 'Deleting...' : 'Delete entry'}
                      </button>
                    </div>
                  ) : isLoadingBillDetails ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
                      <p className="text-gray-500">Loading bill details...</p>
                    </div>
                  ) : selectedBill && (selectedBill.billNumber || selectedBill.orderNumber || selectedBill.docNumber) ? (
                    <>
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-semibold text-gray-900">{selectedBill.billNumber || selectedBill.orderNumber || selectedBill.docNumber || `BILL-${selectedBill.id?.substring(0, 8).toUpperCase() || 'N/A'}`}</h2>
                          <p className="text-gray-500 mt-1">{selectedBill.customerName || 'Unknown Customer'}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                            <Calendar className="w-4 h-4" />
                            <span className="font-medium">{selectedBill.date}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Information</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Bill Date:</span>
                              <span className="font-medium text-gray-700">{selectedBill.date || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Order Number:</span>
                              <span className="font-medium text-gray-700">{selectedBill.orderNumber || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Table Number:</span>
                              <span className="font-medium text-gray-700">{selectedBill.tableNumber || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Site:</span>
                              <span className="font-medium text-gray-700">{selectedBill.siteName || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Status:</span>
                              <span className="px-2 py-1 rounded-full text-xs font-semibold border bg-green-100 text-green-700 border-green-200">
                                Active
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                          <div className="space-y-1 text-sm text-gray-700">
                            <p className="font-medium">{selectedBill.customerName || 'Unknown Customer'}</p>
                            {selectedBill.customerMobile && (
                              <p className="text-gray-600">Mobile: {selectedBill.customerMobile}</p>
                            )}
                            {selectedBill.customerEmail && (
                              <p className="text-gray-600">Email: {selectedBill.customerEmail}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                        {selectedBill.items && selectedBill.items.length > 0 ? (
                          <div className="overflow-x-auto rounded-2xl border border-gray-200">
                            <table className="min-w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-left text-gray-600">
                                  <th className="px-4 py-3 font-semibold">S.No</th>
                                  <th className="px-4 py-3 font-semibold">Item Name</th>
                                  <th className="px-4 py-3 font-semibold">Quantity</th>
                                  <th className="px-4 py-3 font-semibold">Unit</th>
                                  <th className="px-4 py-3 font-semibold">Rate</th>
                                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedBill.items.map((item, index) => (
                                  <tr key={index} className="border-t border-gray-100">
                                    <td className="px-4 py-3">{index + 1}</td>
                                    <td className="px-4 py-3">{item.name || 'N/A'}</td>
                                    <td className="px-4 py-3">{item.quantity || 0}</td>
                                    <td className="px-4 py-3">{item.unit || 'pcs'}</td>
                                    <td className="px-4 py-3">₹{(item.rate || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right">₹{(item.amount || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                                  <td colSpan={5} className="px-4 py-3 font-semibold">Subtotal</td>
                                  <td className="px-4 py-3 text-right font-semibold">₹{(selectedBill.subtotal || 0).toFixed(2)}</td>
                                </tr>
                                {selectedBill.discountValue > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3 font-semibold">Discount ({selectedBill.discountCode || 'N/A'})</td>
                                    <td className="px-4 py-3 text-right font-semibold">-₹{(selectedBill.discountValue || 0).toFixed(2)}</td>
                                  </tr>
                                )}
                                {selectedBill.cgstAmount > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3">CGST ({selectedBill.cgstRate}%)</td>
                                    <td className="px-4 py-3 text-right">₹{(selectedBill.cgstAmount || 0).toFixed(2)}</td>
                                  </tr>
                                )}
                                {selectedBill.sgstAmount > 0 && (
                                  <tr className="border-t border-gray-100 bg-white text-gray-700">
                                    <td colSpan={5} className="px-4 py-3">SGST ({selectedBill.sgstRate}%)</td>
                                    <td className="px-4 py-3 text-right">₹{(selectedBill.sgstAmount || 0).toFixed(2)}</td>
                                  </tr>
                                )}
                                <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                                  <td colSpan={5} className="px-4 py-3">Grand Total</td>
                                  <td className="px-4 py-3 text-right">₹{(selectedBill.grandTotal || 0).toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">No line items found</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No bill data available</p>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default Fdoc;

